import { _ as _toConsumableArray } from '../../unsorted/toConsumableArray.js';
import { _ as _slicedToArray } from '../../unsorted/slicedToArray.js';
import { _ as _defineProperty } from '../../unsorted/defineProperty.js';
import { _ as _objectWithoutProperties } from '../../unsorted/objectWithoutProperties.js';
import { useTheme as useTheme$1, ClassNames } from '@emotion/react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMemo, useState, useCallback, Children, useId, cloneElement, isValidElement, useEffect } from 'react';
import { f as flattenChildren } from '../../unsorted/index3.js';
import { m as merge } from '../../unsorted/index.js';
import { DEFAULT_MENU_ITEM_SIZE, MENU_ITEM_SIZES, TEXT_INPUT_SIZES, DEFAULT_TEXT_INPUT_SIZE, SELECT_SIZES, DEFAULT_SELECT_SIZE, DEFAULT_TEXT_INPUT_ALIGN, DEFAULT_BUTTON_SIZE, DEFAULT_DRAWER_POSITION } from '../../constants.js';
import { useGetSubcomponentChild } from '../../hooks/useGetSubcompnentChild.js';
import { useGetSubcomponentChildren } from '../../hooks/useGetSubcomponentChildren.js';
import { useSplitApartChildrenAndSubComponents } from '../../hooks/useSplitApartChildrenAndSubComponents.js';
import { useTheme } from '../../hooks/useTheme.js';
import { warnUser, noop } from '../../utils/componentHelpers.js';
import { getResponsiveSx, getStartingSize } from '../../utils/styleHelpers.js';
import { getSubcomponentChild, isChildSubcomponent } from '../../utils/subcomponentHelpers.js';
import { lineClamp, vFlex } from '../../utils/sxChunks.js';
import { d as renderResponsiveInputSx, e as renderInputSx, r as renderResponsiveContainerStyles, b as renderContainerStyles, f as baseInputSx, c as inputContainerBaseSx, i as inputIconBaseSx } from '../styles/styles2.js';
import { jsx, jsxs, Fragment } from '@emotion/react/jsx-runtime';
import { A as Avatar } from '../Avatar/Avatar.js';
import { _ as _extends, a as _objectDestructuringEmpty } from '../../unsorted/extends.js';
import { B as Badge } from '../Badge/Badge.js';
import { B as Body } from '../Body/Body.js';
import { B as Box } from '../Box/Box.js';
import { I as Icon } from '../Icon/Icon.js';
import { F as FramedImage } from '../FramedImage/FramedImage.js';
import { useGetCurrentSizeClass } from '../../hooks/useGetCurrentSizeClass.js';
import { i as isImageResizerProps } from '../../utils/imageHelpers2.js';
import { L as Logo } from '../Logo/Logo2.js';
import { F as FramedStack } from '../FramedStack/FramedStack.js';
import { P as PriceDisplay } from '../PriceDisplay/PriceDisplay.js';
import { S as SmartClone } from '../SmartClone/SmartClone.js';
import { u as useWindowSizeStore } from '../../providers/windowSizeContext/windowSizeContext.js';
import { S as ShimmerBox } from '../ShimmerBox/ShimmerBox.js';
import { S as StatefulButtCon } from '../StatefulButtCon/StatefulButtCon.js';
import { S as SwapStack } from '../SwapStack/SwapStack.js';
import { S as Stack } from '../Stack/Stack.js';
import { S as SvgIcon } from '../SvgIcon/SvgIcon.js';
import { D as Divider } from '../Divider/Divider.js';
import { T as Toggle } from '../Toggle/Toggle.js';
import { C as Checkbox } from '../Checkbox/Checkbox.js';
import { R as Radio } from '../Radio/Radio.js';
import { D as Drawer } from '../Drawer/Drawer.js';
import { P as Popover } from '../Popover/Popover.js';
import { C as Chip, i as isBaseClickable, a as isBox } from '../Chip/Chip.js';
import { useBrowserLayoutEffect } from '../../hooks/useBrowserLayoutEffect.js';
import { useBrowserEffect } from '../../hooks/useBrowserEffect.js';
import { useForwardLocalDomRef } from '../../hooks/useForwardLocalDomRef.js';
import { useResizeObserver } from '../../hooks/useResizeObserver.js';
import { B as ButtCon } from '../ButtCon/ButtCon.js';
import { B as BaseClickable } from '../BaseClickable/BaseClickable.js';

function ownKeys$k(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$k(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$k(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$k(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var innerTextContainerSxProps = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "center",
  flexGrow: 1
};
var renderSizeBasedStyles = function renderSizeBasedStyles(_ref) {
  var size = _ref.size,
    theme = _ref.theme;
  switch (size) {
    case "xSmall":
      return {
        paddingTop: theme.base.spacing.x1,
        paddingBottom: theme.base.spacing.x1,
        minHeight: theme.base.spacing.x12
      };
    case "small":
      return {
        paddingTop: theme.base.spacing.x3,
        paddingBottom: theme.base.spacing.x3,
        minHeight: theme.base.spacing.x18
      };
    case "medium":
    default:
      return {
        paddingTop: theme.base.spacing.x6,
        paddingBottom: theme.base.spacing.x6,
        minHeight: theme.base.spacing.x24
      };
  }
};
var baseStyles = {
  display: "flex",
  alignItems: "center",
  paddingX: "base.spacing.x3",
  width: "100%",
  gap: "base.spacing.x4",
  brad: "base.borderRadius.x4",
  transitionProperty: "border, background, box-shadow",
  transitionDuration: "base.motion.normal.gentle.cssDuration",
  transitionTimingFunction: "base.motion.normal.gentle.cssEase",
  border: "solid transparent",
  borderWidth: "base.border.size.200",
  "&.MenuItem--emphasized": {
    bgc: "base.color.translucent.emphasis.100"
  },
  "div&": {
    cursor: "pointer"
  },
  "&:not(article).MenuItem--hover": {
    bgc: "base.color.translucent.emphasis.200",
    boxShadow: "base.shadow.200"
  },
  "&.MenuItem--selected": {
    bgc: "transparent",
    borderColor: "base.color.translucent.standard.1000",
    "&.MenuItem--emphasized": {
      bgc: "base.color.translucent.emphasis.100"
    }
  },
  "&.MenuItem--selected:not(article).MenuItem--hover": {
    boxShadow: "base.shadow.200"
  },
  "&:not(article):active": {
    bgc: "base.color.translucent.emphasis.100",
    boxShadow: "base.shadow.100"
  }
};
var renderResponsiveStyles = function renderResponsiveStyles(_ref2) {
  var size = _ref2.size,
    theme = _ref2.theme;
  return getResponsiveSx({
    size: size,
    theme: theme,
    renderSxAtSize: renderSizeBasedStyles
  });
};
var baseFramedIconStyles = {
  d: "flex",
  justifyContent: "center",
  alignItems: "center",
  bg: "base.color.translucent.standard.100",
  flexShrink: 0
};
var baseLeftImageStyles = {
  flexShrink: 0
};
var baseShimmerOuterSx = {
  position: "relative",
  w: "100%"
};
function renderShimmerOuterStyles(_ref3) {
  var size = _ref3.size;
  switch (size) {
    case "xSmall":
      return {
        height: "50px",
        borderRadius: "base.borderRadius.x4"
      };
    case "small":
      return {
        height: "72px",
        borderRadius: "base.borderRadius.x6"
      };
    case "medium":
    default:
      return {
        height: "96px",
        borderRadius: "base.borderRadius.x6"
      };
  }
}
var renderResponsiveOuterStyles = function renderResponsiveOuterStyles(props) {
  return getResponsiveSx(_objectSpread$k(_objectSpread$k({}, props), {}, {
    renderSxAtSize: renderShimmerOuterStyles
  }));
};
var shimmerLeftMaskSx = {
  display: "block",
  width: "60%",
  height: "100%",
  WebkitMaskPosition: "left center",
  WebkitMaskRepeat: "no-repeat",
  WebkitMaskSize: "contain"
};
var shimmerRightMaskSx = {
  position: "absolute",
  width: "40%",
  height: "100%",
  top: "0",
  right: "0",
  display: "block",
  WebkitMaskPosition: "right center",
  WebkitMaskRepeat: "no-repeat",
  WebkitMaskSize: "contain"
};
function getFramedContainerSx(_ref4) {
  var size = _ref4.size,
    base = _ref4.theme.base;
  switch (size) {
    case "xSmall":
      return {
        width: base.icon.size[400],
        height: base.icon.size[400]
      };
    case "small":
    case "medium":
    default:
      return {
        width: base.icon.size[500],
        height: base.icon.size[500]
      };
  }
}
var getResponsiveFramedContainerSx = function getResponsiveFramedContainerSx(props) {
  return getResponsiveSx(_objectSpread$k(_objectSpread$k({}, props), {}, {
    renderSxAtSize: getFramedContainerSx
  }));
};
function getFramedLogoSx(_ref5) {
  var size = _ref5.size,
    base = _ref5.theme.base;
  switch (size) {
    case "xSmall":
      return {
        width: base.icon.size[300],
        height: base.icon.size[300]
      };
    case "small":
    case "medium":
    default:
      return {
        width: base.icon.size[400],
        height: base.icon.size[400]
      };
  }
}
var getResponsiveFramedLogoSx = function getResponsiveFramedLogoSx(props) {
  return getResponsiveSx(_objectSpread$k(_objectSpread$k({}, props), {}, {
    renderSxAtSize: getFramedContainerSx
  }));
};
var labelAndCaptionBaseSx = _objectSpread$k(_objectSpread$k({}, lineClamp(2)), {}, {
  textAlign: "left"
});
var captionSx = _objectSpread$k(_objectSpread$k({}, labelAndCaptionBaseSx), {}, {
  c: "base.color.text.body.secondary"
});
var labelSx = _objectSpread$k(_objectSpread$k({}, labelAndCaptionBaseSx), {}, {
  c: "base.color.text.body.primary"
});

var containerSx = {
  transitionProperty: baseStyles.transitionProperty,
  transitionDuration: baseStyles.transitionDuration,
  transitionTimingFunction: baseStyles.transitionTimingFunction,
  width: "100%",
  cursor: "pointer",
  "& .VerticalMenu.Box": {
    padding: "unset"
  },
  // @NOTE: apply incremental amounts of left
  // indentation to indicate nested heriarchy
  // @TODO: do we need to allow deeper than 3/4 levels?
  "& .VerticalMenu .MenuItem": {
    paddingLeft: "base.spacing.x5"
  },
  "& .VerticalMenu .ExpandableMenuItem .VerticalMenu .MenuItem": {
    paddingLeft: "base.spacing.x10"
  },
  "& .VerticalMenu .ExpandableMenuItem .VerticalMenu .ExpandableMenuItem .VerticalMenu .MenuItem": {
    paddingLeft: "base.spacing.x15"
  },
  "& .VerticalMenu .ExpandableMenuItem .VerticalMenu .ExpandableMenuItem .VerticalMenu .ExpandableMenuItem .VerticalMenu .MenuItem": {
    paddingLeft: "base.spacing.x20"
  }
};
var rightChevronSx = {
  position: "absolute",
  right: "base.spacing.x3",
  w: "18px",
  stroke: "base.color.text.body.secondary",
  strokeWidth: 2.3,
  fill: "transparent"
};
var nestedVerticalMenuSx = {
  overflow: "visible",
  p: "0",
  bg: "transparent",
  maxh: "unset",
  boxShadow: "unset",
  brad: "unset"
};
var renderSizeBasedContainerStyles = function renderSizeBasedContainerStyles(_ref) {
  var size = _ref.size;
  switch (size) {
    case "xSmall":
      return {
        borderRadius: "base.borderRadius.x4"
      };
    case "small":
    case "medium":
    default:
      return {
        borderRadius: "base.borderRadius.x6"
      };
  }
};
var renderContainerResponsiveStyles = function renderContainerResponsiveStyles(_ref2) {
  var size = _ref2.size,
    theme = _ref2.theme;
  return getResponsiveSx({
    size: size,
    theme: theme,
    renderSxAtSize: renderSizeBasedContainerStyles
  });
};

function ownKeys$j(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$j(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$j(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$j(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var inputBarBaseSx = {
  position: "relative",
  d: "flex",
  width: "100%",
  "&:has(.actualInput:checked) .MenuItem": _objectSpread$j({}, baseStyles["&.MenuItem--selected"]),
  "&:not(.InputBar--disabled):has(.actualInput:checked):hover .MenuItem": _objectSpread$j({}, baseStyles["&.MenuItem--selected:not(article).MenuItem--hover"]),
  "&:not(.InputBar--disabled):hover .MenuItem": _objectSpread$j({}, baseStyles["&:not(article).MenuItem--hover"])
};

function getImageSizeForMenuItemSize(menuItemSize) {
  // xSmall MenuItem size === 'medium' Image size
  // small MenuItem size === 'large' Image size
  // medium MenuItem size === 'large' Image size
  switch (menuItemSize) {
    case "xSmall":
      return "medium";
    case "small":
    case "medium":
      return "large";
    case null:
    default:
      return null;
  }
}
function useGetBiomeImageSizeFromMenuItemSize(size) {
  var chosenBiomeImageSize = useMemo(function () {
    return isMenuItemSizeAnArray(size) ? size.map(function (s) {
      return getImageSizeForMenuItemSize(s);
    }) : getImageSizeForMenuItemSize(size);
  }, [size]);
  return chosenBiomeImageSize;
}
function isMenuItemSizeAnArray(size) {
  return Array.isArray(size);
}
var pickLabelTextSizeFromMenuItemSize = function pickLabelTextSizeFromMenuItemSize(size) {
  switch (size) {
    case null:
      return null;
    case "xSmall":
      return "small";
    case "medium":
    case "small":
    default:
      return "medium";
  }
};
var pickCaptionTextSizeFromMenuItemSize = function pickCaptionTextSizeFromMenuItemSize(size) {
  switch (size) {
    case null:
      return null;
    case "xSmall":
      return "xSmall";
    case "medium":
    case "small":
    default:
      return "small";
  }
};
function useGetTextSizeFromMenuItemSize(menuItemSize, picker, size) {
  var bodyTextSize = useMemo(function () {
    if (size) return size;
    return isMenuItemSizeAnArray(menuItemSize) ? menuItemSize.map(function (s) {
      return picker(s);
    }) : picker(menuItemSize);
  }, [size, menuItemSize, picker]);
  return bodyTextSize;
}
function useGetLabelTextSizeFromMenuItemSize(menuItemSize, size) {
  return useGetTextSizeFromMenuItemSize(menuItemSize, pickLabelTextSizeFromMenuItemSize, size);
}
function useGetCaptionTextSizeFromMenuItemSize(menuItemSize, size) {
  return useGetTextSizeFromMenuItemSize(menuItemSize, pickCaptionTextSizeFromMenuItemSize, size);
}

var _excluded$g = ["sx", "rc", "size"];
function ownKeys$i(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$i(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$i(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$i(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function MenuItemAvatar(_ref) {
  var _ref$sx = _ref.sx,
    sx = _ref$sx === void 0 ? {} : _ref$sx,
    _ref$rc = _ref.rc,
    rc = _ref$rc === void 0 ? jsx("span", {}) : _ref$rc,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? DEFAULT_MENU_ITEM_SIZE : _ref$size,
    props = _objectWithoutProperties(_ref, _excluded$g);
  var _ref2 = "imageUrl" in props ? props : _objectSpread$i(_objectSpread$i({}, props), {}, {
      imageUrl: undefined
    }),
    imageUrl = _ref2.imageUrl;
  var mergedSx = useMemo(function () {
    return merge(baseLeftImageStyles, sx);
  }, [sx]);
  var avatarSize = useGetBiomeImageSizeFromMenuItemSize(size);
  return jsx(Avatar, _objectSpread$i(_objectSpread$i({}, props), {}, {
    imageUrl: imageUrl,
    rc: rc,
    sx: mergedSx,
    size: avatarSize
  }));
}
MenuItemAvatar.displayName = "MenuItem.Avatar";

function ownKeys$h(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$h(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$h(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$h(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function MenuItemBadge(_ref) {
  var props = _extends({}, (_objectDestructuringEmpty(_ref), _ref));
  return jsx(Badge, _objectSpread$h({}, props));
}
MenuItemBadge.displayName = "MenuItem.Badge";

var _excluded$f = ["menuItemSize", "size", "sx"];
function ownKeys$g(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$g(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$g(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$g(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function MenuItemCaption(_ref) {
  var _ref$menuItemSize = _ref.menuItemSize,
    menuItemSize = _ref$menuItemSize === void 0 ? DEFAULT_MENU_ITEM_SIZE : _ref$menuItemSize,
    size = _ref.size,
    _ref$sx = _ref.sx,
    sx = _ref$sx === void 0 ? {} : _ref$sx,
    props = _objectWithoutProperties(_ref, _excluded$f);
  var mergedSx = useMemo(function () {
    return merge(captionSx, sx);
  }, [sx]);
  var bodyTextSize = useGetCaptionTextSizeFromMenuItemSize(menuItemSize, size);
  return jsx(Body, _objectSpread$g(_objectSpread$g({}, props), {}, {
    sx: mergedSx,
    size: bodyTextSize
  }));
}
MenuItemCaption.displayName = "MenuItem.Caption";

var _excluded$e = ["icon", "sx", "testId", "variant", "circularFrame", "size", "rc"];
function ownKeys$f(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$f(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$f(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$f(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function MenuItemFramedIcon(_ref) {
  var icon = _ref.icon,
    _ref$sx = _ref.sx,
    sx = _ref$sx === void 0 ? {} : _ref$sx,
    testId = _ref.testId,
    variant = _ref.variant,
    circularFrame = _ref.circularFrame,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? DEFAULT_MENU_ITEM_SIZE : _ref$size,
    _ref$rc = _ref.rc,
    rc = _ref$rc === void 0 ? jsx("span", {}) : _ref$rc,
    props = _objectWithoutProperties(_ref, _excluded$e);
  var theme = useTheme();
  var startingSize = getStartingSize(size, DEFAULT_MENU_ITEM_SIZE, MENU_ITEM_SIZES);
  var allSx = merge(baseFramedIconStyles, getFramedContainerSx({
    theme: theme,
    size: startingSize
  }), getResponsiveFramedContainerSx({
    theme: theme,
    size: size
  }), {
    borderRadius: circularFrame ? "50%" : "base.borderRadius.x2"
  }, sx);
  return jsx(Box, _objectSpread$f(_objectSpread$f({}, props), {}, {
    rc: rc,
    testId: testId,
    sx: allSx,
    children: jsx(Icon, {
      icon: icon,
      variant: variant,
      sx: {
        width: "base.icon.size.300",
        fill: "base.color.text.body.primary"
      }
    })
  }));
}
MenuItemFramedIcon.displayName = "MenuItem.FramedIcon";

var _excluded$d = ["use", "sx", "size"];
function ownKeys$e(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$e(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$e(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$e(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function MenuItemFramedImage(_ref) {
  var use = _ref.use,
    _ref$sx = _ref.sx,
    sx = _ref$sx === void 0 ? {} : _ref$sx,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? DEFAULT_MENU_ITEM_SIZE : _ref$size,
    props = _objectWithoutProperties(_ref, _excluded$d);
  var theme = useTheme();
  var startingSize = getStartingSize(size, DEFAULT_MENU_ITEM_SIZE, MENU_ITEM_SIZES);
  var mergedSx = useMemo(function () {
    return merge(baseLeftImageStyles, getFramedContainerSx({
      theme: theme,
      size: startingSize
    }), getResponsiveFramedContainerSx({
      theme: theme,
      size: size
    }), sx);
  }, [sx, size, theme, startingSize]);
  var shouldUseImageResizer = isImageResizerProps(use);
  var currentSizeClass = useGetCurrentSizeClass(size, DEFAULT_MENU_ITEM_SIZE, MENU_ITEM_SIZES);
  var typedProps = useMemo(function () {
    return shouldUseImageResizer ? _objectSpread$e(_objectSpread$e({}, props), {}, {
      relativeImageSizeInLayout: currentSizeClass === "xSmall" ? theme.base.icon.size[400] : theme.base.icon.size[500]
    }) : _objectSpread$e(_objectSpread$e({}, props), {}, {
      use: use
    });
  }, [use, props, currentSizeClass, shouldUseImageResizer, theme.base.icon.size]);
  return jsx(FramedImage, _objectSpread$e(_objectSpread$e({}, typedProps), {}, {
    sx: mergedSx
  }));
}
MenuItemFramedImage.displayName = "MenuItem.FramedImage";

var _excluded$c = ["logo", "sx", "testId", "circularFrame", "size", "rc"];
function ownKeys$d(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$d(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$d(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$d(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function MenuItemFramedLogo(_ref) {
  var logo = _ref.logo,
    _ref$sx = _ref.sx,
    sx = _ref$sx === void 0 ? {} : _ref$sx,
    testId = _ref.testId,
    _ref$circularFrame = _ref.circularFrame,
    circularFrame = _ref$circularFrame === void 0 ? false : _ref$circularFrame,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? DEFAULT_MENU_ITEM_SIZE : _ref$size,
    _ref$rc = _ref.rc,
    rc = _ref$rc === void 0 ? jsx("span", {}) : _ref$rc,
    props = _objectWithoutProperties(_ref, _excluded$c);
  var theme = useTheme();
  var startingSize = getStartingSize(size, DEFAULT_MENU_ITEM_SIZE, MENU_ITEM_SIZES);
  var allSx = useMemo(function () {
    return merge(baseFramedIconStyles, getFramedContainerSx({
      theme: theme,
      size: startingSize
    }), getResponsiveFramedContainerSx({
      theme: theme,
      size: size
    }), {
      overflow: "hidden",
      bg: "base.color.fixed.black.1000",
      borderRadius: circularFrame ? "50%" : "base.borderRadius.x2",
      flexShrink: 0
    }, sx);
  }, [sx, size, theme, startingSize, circularFrame]);
  var allLogoSx = merge(getFramedLogoSx({
    theme: theme,
    size: startingSize
  }), getResponsiveFramedLogoSx({
    theme: theme,
    size: size
  }));
  return jsx(Box, _objectSpread$d(_objectSpread$d({}, props), {}, {
    testId: testId,
    sx: allSx,
    rc: rc,
    children: jsx(Logo, {
      logo: logo,
      sx: allLogoSx
    })
  }));
}
MenuItemFramedLogo.displayName = "MenuItem.FramedLogo";

var _excluded$b = ["sx", "rc", "size"];
function ownKeys$c(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$c(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$c(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$c(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function MenuItemFramedStack(_ref) {
  var _ref$sx = _ref.sx,
    sx = _ref$sx === void 0 ? {} : _ref$sx,
    _ref$rc = _ref.rc,
    rc = _ref$rc === void 0 ? jsx("span", {}) : _ref$rc,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? DEFAULT_MENU_ITEM_SIZE : _ref$size,
    props = _objectWithoutProperties(_ref, _excluded$b);
  var mergedSx = useMemo(function () {
    return merge(baseLeftImageStyles, sx);
  }, [sx]);
  var framedStackSize = useGetBiomeImageSizeFromMenuItemSize(size);
  return jsx(FramedStack, _objectSpread$c(_objectSpread$c({}, props), {}, {
    rc: rc,
    sx: mergedSx,
    size: framedStackSize
  }));
}
MenuItemFramedStack.displayName = "MenuItem.FramedStack";
MenuItemFramedStack.PrimaryImage = FramedStack.PrimaryImage;
MenuItemFramedStack.SecondaryImage = FramedStack.SecondaryImage;
MenuItemFramedStack.TertiaryImage = FramedStack.TertiaryImage;

var _excluded$a = ["sx"];
function ownKeys$b(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$b(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$b(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$b(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function MenuItemIcon(_ref) {
  var _ref$sx = _ref.sx,
    sx = _ref$sx === void 0 ? {} : _ref$sx,
    props = _objectWithoutProperties(_ref, _excluded$a);
  var mergedSx = useMemo(function () {
    return merge({
      width: "base.icon.size.300",
      fill: "base.color.text.body.primary",
      flexShrink: 0
    }, sx);
  }, [sx]);
  return jsx(Icon, _objectSpread$b(_objectSpread$b({}, props), {}, {
    sx: mergedSx
  }));
}
MenuItemIcon.displayName = "MenuItem.Icon";

var _excluded$9 = ["icon", "sx"];
function ownKeys$a(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$a(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$a(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$a(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function MenuItemIntentIcon(_ref) {
  var _ref$icon = _ref.icon,
    icon = _ref$icon === void 0 ? "ChevronForward" : _ref$icon,
    _ref$sx = _ref.sx,
    sx = _ref$sx === void 0 ? {} : _ref$sx,
    props = _objectWithoutProperties(_ref, _excluded$9);
  return jsx(Icon, _objectSpread$a(_objectSpread$a({}, props), {}, {
    icon: icon,
    sx: merge({
      width: "base.icon.size.200",
      fill: "base.color.text.body.secondary",
      flexShrink: 0
    }, sx)
  }));
}
MenuItemIntentIcon.displayName = "MenuItem.IntentIcon";

var _excluded$8 = ["size", "menuItemSize", "children", "testId", "sx"];
function ownKeys$9(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$9(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$9(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$9(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function MenuItemLabel(_ref) {
  var size = _ref.size,
    _ref$menuItemSize = _ref.menuItemSize,
    menuItemSize = _ref$menuItemSize === void 0 ? DEFAULT_MENU_ITEM_SIZE : _ref$menuItemSize,
    children = _ref.children,
    _ref$testId = _ref.testId,
    testId = _ref$testId === void 0 ? "MenuItemLabel" : _ref$testId,
    _ref$sx = _ref.sx,
    sx = _ref$sx === void 0 ? {} : _ref$sx,
    props = _objectWithoutProperties(_ref, _excluded$8);
  var bodyTextSize = useGetLabelTextSizeFromMenuItemSize(menuItemSize, size);
  var mergedSx = useMemo(function () {
    return merge(labelSx, sx);
  }, [sx]);
  return jsx(Body, _objectSpread$9(_objectSpread$9({}, props), {}, {
    sx: mergedSx,
    testId: testId,
    size: bodyTextSize,
    children: children
  }));
}
MenuItemLabel.displayName = "MenuItem.Label";

var _excluded$7 = ["sx", "size", "menuItemSize", "price", "children", "use"];
function ownKeys$8(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$8(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$8(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$8(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function MenuItemPriceDisplay(_ref) {
  var _ref$sx = _ref.sx,
    sx = _ref$sx === void 0 ? {} : _ref$sx,
    size = _ref.size,
    _ref$menuItemSize = _ref.menuItemSize,
    menuItemSize = _ref$menuItemSize === void 0 ? DEFAULT_MENU_ITEM_SIZE : _ref$menuItemSize,
    price = _ref.price,
    children = _ref.children,
    use = _ref.use,
    props = _objectWithoutProperties(_ref, _excluded$7);
  var bodyTextSize = useGetLabelTextSizeFromMenuItemSize(menuItemSize, size);
  var caption = useGetSubcomponentChild(children, PriceDisplay.Caption);
  var _useSplitApartChildre = useSplitApartChildrenAndSubComponents(children, [PriceDisplay.Caption]),
    otherChildren = _useSplitApartChildre.otherChildren;
  var captionTextSize = useGetCaptionTextSizeFromMenuItemSize(menuItemSize, caption === null || caption === void 0 ? void 0 : caption.props.size);
  var mergedSx = useMemo(function () {
    return merge({
      whiteSpace: "nowrap"
    }, sx);
  }, [sx]);
  return jsxs(PriceDisplay, _objectSpread$8(_objectSpread$8(_objectSpread$8({}, props), !use && {
    size: bodyTextSize
  }), {}, {
    use: use,
    sx: mergedSx,
    price: price,
    children: [caption && jsx(SmartClone, {
      size: captionTextSize,
      children: caption
    }), otherChildren]
  }));
}
MenuItemPriceDisplay.displayName = "MenuItem.PriceDisplay";
MenuItemPriceDisplay.CurrencyImage = PriceDisplay.CurrencyImage;
MenuItemPriceDisplay.Icon = PriceDisplay.Icon;
MenuItemPriceDisplay.Caption = PriceDisplay.Caption;

var _excluded$6 = ["size", "emphasized", "sx", "testId"];
function ownKeys$7(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$7(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$7(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$7(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var svgMasks = {
  xSmall: {
    left: "url('data:image/svg+xml;utf8, <svg viewBox=\"0 0 196 50\" xmlns=\"http://www.w3.org/2000/svg\" version=\"1.1\"><path d=\"M24 41C32.8366 41 40 33.8366 40 25C40 16.1634 32.8366 9 24 9C15.1634 9 8 16.1634 8 25C8 33.8366 15.1634 41 24 41ZM58 19C56.8954 19 56 19.8954 56 21V29C56 30.1046 56.8954 31 58 31H174C175.105 31 176 30.1046 176 29V21C176 19.8954 175.105 19 174 19H58Z\" /></svg>')",
    right: "url('data:image/svg+xml;utf8, <svg viewBox=\"0 0 50 50\" xmlns=\"http://www.w3.org/2000/svg\" version=\"1.1\"><path d=\"M38 25C38 31.6274 32.6274 37 26 37C19.3726 37 14 31.6274 14 25C14 18.3726 19.3726 13 26 13C32.6274 13 38 18.3726 38 25Z\" /></svg>')"
  },
  small: {
    left: "url('data:image/svg+xml;utf8, <svg viewBox=\"0 0 196 72\" xmlns=\"http://www.w3.org/2000/svg\" version=\"1.1\"><path d=\"M36 60C49.2548 60 60 49.2548 60 36C60 22.7452 49.2548 12 36 12C22.7452 12 12 22.7452 12 36C12 49.2548 22.7452 60 36 60ZM76 24C76 21.7909 77.7909 20 80 20H192C194.209 20 196 21.7909 196 24V32C196 34.2091 194.209 36 192 36H80C77.7909 36 76 34.2091 76 32V24ZM75 45C75 42.7909 76.7909 41 79 41H135C137.209 41 139 42.7909 139 45V49C139 51.2091 137.209 53 135 53H79C76.7909 53 75 51.2091 75 49V45Z\" /></svg>')",
    right: "url('data:image/svg+xml;utf8, <svg viewBox=\"0 0 126 72\" xmlns=\"http://www.w3.org/2000/svg\" version=\"1.1\"><path d=\"M98 52C106.837 52 114 44.8366 114 36C114 27.1634 106.837 20 98 20C89.1634 20 82 27.1634 82 36C82 44.8366 89.1634 52 98 52ZM30 30C27.7909 30 26 31.7909 26 34V38C26 40.2091 27.7909 42 30 42H70C72.2091 42 74 40.2091 74 38V34C74 31.7909 72.2091 30 70 30H30Z\" /></svg>')"
  },
  medium: {
    left: "url('data:image/svg+xml;utf8, <svg viewBox=\"0 0 196 96\" xmlns=\"http://www.w3.org/2000/svg\" version=\"1.1\"><path d=\"M36 72C49.2548 72 60 61.2548 60 48C60 34.7452 49.2548 24 36 24C22.7452 24 12 34.7452 12 48C12 61.2548 22.7452 72 36 72ZM76 36C76 33.7909 77.7909 32 80 32H192C194.209 32 196 33.7909 196 36V44C196 46.2091 194.209 48 192 48H80C77.7909 48 76 46.2091 76 44V36ZM76 58C76 55.7909 77.7909 54 80 54H136C138.209 54 140 55.7909 140 58V62C140 64.2091 138.209 66 136 66H80C77.7909 66 76 64.2091 76 62V58Z\" /></svg>')",
    right: "url('data:image/svg+xml;utf8, <svg viewBox=\"0 0 126 96\" xmlns=\"http://www.w3.org/2000/svg\" version=\"1.1\"><path d=\"M98 64C106.837 64 114 56.8366 114 48C114 39.1634 106.837 32 98 32C89.1634 32 82 39.1634 82 48C82 56.8366 89.1634 64 98 64ZM30 42C27.7909 42 26 43.7909 26 46V50C26 52.2091 27.7909 54 30 54H70C72.2091 54 74 52.2091 74 50V46C74 43.7909 72.2091 42 70 42H30Z\" /></svg>')"
  }
};
function MenuItemShimmer(_ref) {
  var _ref$size = _ref.size,
    size = _ref$size === void 0 ? DEFAULT_MENU_ITEM_SIZE : _ref$size,
    emphasized = _ref.emphasized,
    _ref$sx = _ref.sx,
    sx = _ref$sx === void 0 ? {} : _ref$sx,
    testId = _ref.testId,
    props = _objectWithoutProperties(_ref, _excluded$6);
  var _useWindowSizeStore = useWindowSizeStore(function (state) {
      return state.width;
    }),
    width = _useWindowSizeStore.state;
  var theme = useTheme$1();
  var breakpointAsArray = theme.base.breakpointAsArray;
  var startingSize = getStartingSize(size, DEFAULT_MENU_ITEM_SIZE, MENU_ITEM_SIZES);
  var mergedOuterSx = useMemo(function () {
    return merge(baseShimmerOuterSx, renderShimmerOuterStyles({
      size: startingSize
    }), renderResponsiveOuterStyles({
      size: size,
      theme: theme
    }), {
      bgc: emphasized ? baseStyles["&.MenuItem--emphasized"].bgc : "transparent"
    }, sx);
  }, [sx, size, theme, startingSize, emphasized]);
  var _useMemo = useMemo(function () {
      var defaultMasks = svgMasks.medium;
      if (width === null) return defaultMasks;
      if (isMenuItemSizeAnArray(size)) {
        var actualResponsiveSizes = _toConsumableArray(size);
        actualResponsiveSizes.shift();
        return actualResponsiveSizes.reduce(function (accum, curr, index) {
          var newAccum = accum;
          if (curr !== null && width >= breakpointAsArray[index]) {
            newAccum = svgMasks[curr];
          }
          return newAccum;
        }, defaultMasks);
      }
      if (size === "medium" || size === "small" || size === "xSmall") {
        return svgMasks[size];
      }
      return defaultMasks;
    }, [breakpointAsArray, size, width]),
    leftMask = _useMemo.left,
    rightMask = _useMemo.right;
  return jsxs(Box, _objectSpread$7(_objectSpread$7({}, props), {}, {
    testId: testId,
    sx: mergedOuterSx,
    children: [jsx(Box, {
      rc: jsx("span", {}),
      testId: "".concat(testId, "__leftMask"),
      sx: _objectSpread$7(_objectSpread$7({}, shimmerLeftMaskSx), {}, {
        WebkitMaskImage: leftMask
      }),
      children: jsx(ShimmerBox, {
        rc: jsx("span", {}),
        testId: "".concat(testId, "__leftMask__shimmer")
      })
    }), jsx(Box, {
      rc: jsx("span", {}),
      testId: "".concat(testId, "__rightMask"),
      sx: _objectSpread$7(_objectSpread$7({}, shimmerRightMaskSx), {}, {
        WebkitMaskImage: rightMask
      }),
      children: jsx(ShimmerBox, {
        rc: jsx("span", {}),
        testId: "".concat(testId, "__rightMask__shimmer")
      })
    })]
  }));
}
MenuItemShimmer.displayName = "MenuItemShimmer";

var _excluded$5 = ["sx", "size"];
function ownKeys$6(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$6(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$6(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$6(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function MenuItemStatefulButtCon(_ref) {
  var _ref$sx = _ref.sx,
    sx = _ref$sx === void 0 ? {} : _ref$sx,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? "small" : _ref$size,
    props = _objectWithoutProperties(_ref, _excluded$5);
  var _ref2 = "onClick" in props ? props : {
      onClick: null
    },
    _onClick = _ref2.onClick;
  var mergedSx = useMemo(function () {
    return merge({
      flexShrink: 0
    }, sx);
  }, [sx]);
  return jsx(StatefulButtCon, _objectSpread$6(_objectSpread$6({}, props), {}, {
    sx: mergedSx,
    onClick: function onClick(ev) {
      ev.stopPropagation();
      if (typeof _onClick === "function") {
        _onClick(ev);
      }
    },
    size: size
  }));
}
MenuItemStatefulButtCon.displayName = "MenuItem.StatefulButtCon";
MenuItemStatefulButtCon.SvgIcon = StatefulButtCon.SvgIcon;

var _excluded$4 = ["sx", "rc", "size"];
function ownKeys$5(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$5(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$5(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$5(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function MenuItemSwapStack(_ref) {
  var _ref$sx = _ref.sx,
    sx = _ref$sx === void 0 ? {} : _ref$sx,
    _ref$rc = _ref.rc,
    rc = _ref$rc === void 0 ? jsx("span", {}) : _ref$rc,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? DEFAULT_MENU_ITEM_SIZE : _ref$size,
    props = _objectWithoutProperties(_ref, _excluded$4);
  var mergedSx = useMemo(function () {
    return merge(baseLeftImageStyles, sx);
  }, [sx]);
  var swapStackSize = useGetBiomeImageSizeFromMenuItemSize(size);
  return jsx(SwapStack, _objectSpread$5(_objectSpread$5({}, props), {}, {
    rc: rc,
    sx: mergedSx,
    size: swapStackSize
  }));
}
MenuItemSwapStack.displayName = "MenuItem.SwapStack";
MenuItemSwapStack.PrimaryImage = SwapStack.PrimaryImage;
MenuItemSwapStack.SecondaryImage = SwapStack.SecondaryImage;

function pickAnOption(selectedOption, options) {
  return typeof selectedOption !== "undefined" && Array.isArray(options) ? options.find(function (option) {
    return option.props.optionKey === selectedOption;
  }) : null;
}
var noOptionFoundErrorText = function noOptionFoundErrorText(selectedOption) {
  return "No <*.Option> subcomponent found for the selectedOption: \"".concat(selectedOption, "\"");
};
function useGetControlledContent(_ref) {
  var options = _ref.options,
    selectedOption = _ref.selectedOption,
    subcomponent = _ref.subcomponent;
  var content = useMemo(function () {
    if (selectedOption === null) return null;
    var pickedOption = pickAnOption(selectedOption, options);
    if (pickedOption !== null && pickedOption !== void 0 && pickedOption.props.children) {
      return getSubcomponentChild(pickedOption.props.children, subcomponent);
    }
    if (selectedOption !== undefined) {
      warnUser(noOptionFoundErrorText(selectedOption));
    }
    return null;
  }, [options, selectedOption, subcomponent]);
  return content;
}

function createMultiSelectErrorMessage(selectedOptions) {
  return selectedOptions.reduce(function (accum, opt) {
    return "".concat(accum, " \n").concat(noOptionFoundErrorText(opt));
  }, "");
}

var chipBaseSx = {
  pos: "relative"
};
var baseMultiSelectSx = {
  pr: "base.spacing.x10",
  cursor: "pointer",
  "&.MultiSelect--selectedChipsWrap": {
    height: "unset",
    py: "base.spacing.x2",
    alignItems: "flex-start",
    "& > .MultiSelect__chipsContainer": {
      flexWrap: "wrap",
      overflowX: "unset"
    },
    "& .Chip__label": {
      whiteSpace: "normal"
    }
  },
  "&.MultiSelect--medium": {
    "&.MultiSelect--selectedChipsWrap": {
      "& .MultiSelect__defaultLabel": {
        mt: "base.spacing.x1"
      },
      "& .MultiSelect__leftIcon": {
        mt: function mt(_ref) {
          var base = _ref.base;
          return "calc(".concat(base.spacing.x1, " - 1px)");
        }
      },
      "& .MultiSelect__chevron": {
        mt: function mt(_ref2) {
          var base = _ref2.base;
          return "calc(".concat(base.spacing.x1, " + 1px)");
        }
      }
    },
    "& > .MultiSelect__chipsContainer": {
      minh: "base.spacing.x8"
    }
  },
  "&.MultiSelect--large": {
    "&.MultiSelect--selectedChipsWrap": {
      "& .MultiSelect__defaultLabel": {
        mt: "base.spacing.x3"
      },
      "& .MultiSelect__leftIcon": {
        mt: function mt(_ref3) {
          var base = _ref3.base;
          return "calc(".concat(base.spacing.x3, " - 1px)");
        }
      },
      "& .MultiSelect__chevron": {
        mt: function mt(_ref4) {
          var base = _ref4.base;
          return "calc(".concat(base.spacing.x3, " + 1px)");
        }
      }
    },
    "& > .MultiSelect__chipsContainer": {
      minh: "base.spacing.x12"
    }
  },
  "& > .MultiSelect__chipsContainer": {
    pos: "relative"
  },
  "& .Chip__label": {
    whiteSpace: "nowrap"
  }
};
var baseChipsContainerSx = {
  pos: "relative",
  flexWrap: "nowrap",
  overflowX: "auto",
  "&::-webkit-scrollbar": {
    display: "none"
  }
};
var defaultLabelSx = {
  c: "base.color.text.body.secondary"
};
var multiSelectOptionSx = {
  "&.MultiSelectOption": {
    "&:has(.actualInput:checked) .MenuItem": {
      borderColor: "transparent"
    }
  }
};

var _excluded$3 = ["className", "use"];
function ownKeys$4(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$4(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$4(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$4(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function SelectDefaultFramedImage(_ref) {
  var className = _ref.className,
    use = _ref.use,
    props = _objectWithoutProperties(_ref, _excluded$3);
  return jsx(MenuItemFramedImage, _objectSpread$4(_objectSpread$4({}, props), {}, {
    className: "".concat(className !== null && className !== void 0 ? className : "", " SelectDefaultFramedImage"),
    use: use
  }));
}
SelectDefaultFramedImage.displayName = "Select.DefaultFramedImage";

var _excluded$2 = ["className"];
function ownKeys$3(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$3(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$3(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$3(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function SelectDefaultFramedLogo(_ref) {
  var className = _ref.className,
    props = _objectWithoutProperties(_ref, _excluded$2);
  return jsx(MenuItemFramedLogo, _objectSpread$3(_objectSpread$3({}, props), {}, {
    className: "".concat(className !== null && className !== void 0 ? className : "", " SelectDefaultFramedLogo")
  }));
}
SelectDefaultFramedLogo.displayName = "Select.DefaultFramedLogo";

var _excluded$1 = ["className"];
function ownKeys$2(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$2(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$2(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$2(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function SelectDefaultIcon(_ref) {
  var className = _ref.className,
    props = _objectWithoutProperties(_ref, _excluded$1);
  return jsx(Icon, _objectSpread$2(_objectSpread$2({}, props), {}, {
    className: "".concat(className !== null && className !== void 0 ? className : "", " SelectDefaultIcon")
  }));
}
SelectDefaultIcon.displayName = "Select.DefaultIcon";

function ownKeys$1(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var selectChevronSx = {
  w: "base.icon.size.250",
  stroke: "base.color.text.body.primary",
  strokeWidth: 2.75,
  fill: "transparent",
  right: "base.spacing.x3"
};
var baseSelectSx = {
  pos: "relative",
  d: "flex",
  alignItems: "center",
  w: "auto"
};
var getSelectCustomSx = function getSelectCustomSx(_ref) {
  var textAlign = _ref.textAlign,
    disabled = _ref.disabled;
  return _objectSpread$1(_objectSpread$1({}, textAlign === "right" ? {
    pr: "base.spacing.x10"
  } : {}), {}, {
    justifyContent: textAlign === "left" ? "flex-start" : "flex-end",
    pointerEvents: disabled ? "none" : "auto"
  });
};
var getResponsiveSelectCustomSx = function getResponsiveSelectCustomSx(props) {
  return getResponsiveSx(_objectSpread$1(_objectSpread$1({}, props), {}, {
    renderSxAtSize: getSelectCustomSx
  }));
};

var verticalMenuSxProps = {
  minWidth: "280px",
  maxHeight: "300px",
  bgc: "base.color.neutral.600",
  position: "relative",
  p: "base.spacing.x2",
  overflowY: "auto",
  brad: "base.borderRadius.x6",
  gap: "base.spacing.x1",
  "&.VerticalMenu--xSmall": {
    p: "base.spacing.x1"
  }
};

var _excluded = ["testId", "className", "children", "emphasized", "selected", "sx", "rc", "size", "controlledHover", "textAlign"],
  _excluded2 = ["shimmer", "size", "rc", "testId", "className", "children", "emphasized", "selected", "sx", "shimmerSx", "controlledHover", "textAlign"],
  _excluded3 = ["children", "sx"],
  _excluded4 = ["children", "sx"],
  _excluded5 = ["children", "size", "emphasized", "textAlign", "expanded", "defaultExpanded", "testId", "onExpandChange", "className", "shimmer", "shimmerSx", "sx"],
  _excluded6 = ["children", "sx", "size", "emphasized", "textAlign", "className"],
  _excluded7 = ["size", "onOptionClick", "children", "optionKey", "sx"],
  _excluded8 = ["size", "onOptionClick", "children", "optionKey", "sx", "className"],
  _excluded9 = ["children", "checked", "size", "emphasized", "disabled", "id", "inputType", "domRef", "inputRef", "onChange", "testId", "defaultChecked", "sx", "textAlign", "className", "rc"],
  _excluded10 = ["children", "domRef", "testId", "icon", "size", "variant", "onClick", "drawerSize", "showHeaderBar", "headerBarTitle", "showBgOverlay", "outsideClicksClose", "escapeKeyClose", "drawerPosition"],
  _excluded11 = ["children", "domRef", "testId", "icon", "size", "variant", "onClick"],
  _excluded12 = ["children", "validationStatus", "defaultLabel", "sx", "popoverContainerSx", "domRef", "onSelectChange", "testId", "id", "size", "textAlign", "targetClickOveride", "showSelectAll", "selectedChipsWrap", "disabled", "className"],
  _excluded13 = ["selectedOptions", "defaultOptions"],
  _excluded14 = ["sx"],
  _excluded15 = ["size", "sx"],
  _excluded16 = ["children", "validationStatus", "defaultLabel", "sx", "popoverContainerSx", "onSelectChange", "domRef", "testId", "id", "size", "textAlign", "targetClickOveride", "disabled", "className"],
  _excluded17 = ["selectedOption", "defaultOption"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function MenuItemContent(_ref) {
  var testId = _ref.testId,
    className = _ref.className,
    children = _ref.children,
    emphasized = _ref.emphasized,
    selected = _ref.selected,
    _ref$sx = _ref.sx,
    sx = _ref$sx === void 0 ? {} : _ref$sx,
    rc = _ref.rc,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? DEFAULT_MENU_ITEM_SIZE : _ref$size,
    controlledHover = _ref.controlledHover,
    _ref$textAlign = _ref.textAlign,
    textAlign = _ref$textAlign === void 0 ? DEFAULT_TEXT_INPUT_ALIGN : _ref$textAlign,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var _ref2 = "onClick" in otherProps ? otherProps : _objectSpread(_objectSpread({}, otherProps), {}, {
      onClick: null
    }),
    onClick = _ref2.onClick;
  var theme = useTheme();
  var startingSize = getStartingSize(size, DEFAULT_MENU_ITEM_SIZE, MENU_ITEM_SIZES);
  var allStyles = merge({}, baseStyles, renderSizeBasedStyles({
    size: startingSize,
    theme: theme
  }), renderResponsiveStyles({
    size: size,
    theme: theme
  }), sx);
  var icon = useGetSubcomponentChild(children, MenuItemIcon);
  var framedIcon = useGetSubcomponentChild(children, MenuItemFramedIcon);
  var framedImage = useGetSubcomponentChild(children, MenuItemFramedImage);
  var framedLogo = useGetSubcomponentChild(children, MenuItemFramedLogo);
  var swapStack = useGetSubcomponentChild(children, MenuItemSwapStack);
  var framedStack = useGetSubcomponentChild(children, MenuItemFramedStack);
  var avatar = useGetSubcomponentChild(children, MenuItemAvatar);
  var label = useGetSubcomponentChild(children, MenuItemLabel);
  var caption = useGetSubcomponentChild(children, MenuItemCaption);
  var intentIcon = useGetSubcomponentChild(children, MenuItemIntentIcon);
  var badge = useGetSubcomponentChild(children, MenuItemBadge);
  var priceDisplay = useGetSubcomponentChild(children, MenuItemPriceDisplay);
  var popoverMenu = useGetSubcomponentChild(children, MenuItemOverflowPopoverMenu);
  var drawerMenu = useGetSubcomponentChild(children, MenuItemOverflowDrawerMenu);
  var statefulButtCons = useGetSubcomponentChildren(children, MenuItemStatefulButtCon);
  var _useSplitApartChildre = useSplitApartChildrenAndSubComponents(children, [MenuItemIcon, MenuItemFramedIcon, MenuItemFramedImage, MenuItemFramedLogo, MenuItemLabel, MenuItemCaption, MenuItemIntentIcon, MenuItemBadge, MenuItemPriceDisplay, MenuItemOverflowPopoverMenu, MenuItemOverflowDrawerMenu, MenuItemStatefulButtCon, MenuItemSwapStack, MenuItemFramedStack, MenuItemAvatar]),
    otherChildren = _useSplitApartChildre.otherChildren;
  var baseClickableProps = _objectSpread(_objectSpread({}, otherProps), {}, {
    onClick: onClick,
    rc: rc
  });
  var boxProps = _objectSpread(_objectSpread({}, otherProps), {}, {
    onClick: onClick
  });
  var containsClickables = Boolean(Array.isArray(statefulButtCons) && statefulButtCons.length) || Boolean(popoverMenu) || Boolean(drawerMenu);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isHovering = _useState2[0],
    setIsHovering = _useState2[1];
  useEffect(function () {
    if (typeof controlledHover !== "undefined") setIsHovering(controlledHover);
  }, [controlledHover]);
  var leftImageContent = useMemo(function () {
    switch (true) {
      case Boolean(avatar):
        return jsx(SmartClone, {
          testId: (avatar === null || avatar === void 0 ? void 0 : avatar.props.testId) || "".concat(testId, "__avatar"),
          size: size,
          children: avatar
        });
      case Boolean(framedIcon):
        return jsx(SmartClone, {
          testId: (framedIcon === null || framedIcon === void 0 ? void 0 : framedIcon.props.testId) || "".concat(testId, "__framedIcon"),
          size: size,
          children: framedIcon
        });
      case Boolean(framedImage):
        return jsx(SmartClone, {
          size: size,
          testId: (framedImage === null || framedImage === void 0 ? void 0 : framedImage.props.testId) || "".concat(testId, "__framedImage"),
          children: framedImage
        });
      case Boolean(framedLogo):
        return jsx(SmartClone, {
          testId: (framedLogo === null || framedLogo === void 0 ? void 0 : framedLogo.props.testId) || "".concat(testId, "__framedLogo"),
          size: size,
          children: framedLogo
        });
      case Boolean(framedStack):
        return jsx(SmartClone, {
          testId: (framedStack === null || framedStack === void 0 ? void 0 : framedStack.props.testId) || "".concat(testId, "__framedStack"),
          size: size,
          children: framedStack
        });
      case Boolean(icon):
        return jsx(SmartClone, {
          testId: (icon === null || icon === void 0 ? void 0 : icon.props.testId) || "".concat(testId, "__icon"),
          children: icon
        });
      case Boolean(swapStack):
        return jsx(SmartClone, {
          testId: (swapStack === null || swapStack === void 0 ? void 0 : swapStack.props.testId) || "".concat(testId, "__swapStack"),
          size: size,
          children: swapStack
        });
      default:
        return null;
    }
  }, [avatar, framedLogo, framedImage, framedIcon, icon, framedStack, swapStack, size, testId]);
  var innerContents = jsxs(Fragment, {
    children: [leftImageContent, jsxs(Box, {
      rc: jsx("span", {}),
      sx: merge(innerTextContainerSxProps, {
        alignItems: textAlign === "left" ? "flex-start" : "flex-end"
      }),
      className: "innerTextContainer",
      children: [label && jsx(SmartClone, {
        testId: label.props.testId || "".concat(testId, "__label"),
        menuItemSize: size,
        children: label
      }), caption && jsx(SmartClone, {
        testId: caption.props.testId || "".concat(testId, "__caption"),
        menuItemSize: size,
        children: caption
      })]
    }), priceDisplay || statefulButtCons || drawerMenu || popoverMenu || badge || intentIcon || otherChildren ? jsxs(Box, {
      sx: {
        d: "flex",
        gap: "base.spacing.x2",
        alignItems: "center",
        flexShrink: 0
      },
      rc: jsx("span", {}),
      className: "rightContainer",
      children: [priceDisplay && jsx(SmartClone, {
        testId: priceDisplay.props.testId || "".concat(testId, "__priceDisplay"),
        menuItemSize: size,
        children: priceDisplay
      }), statefulButtCons === null || statefulButtCons === void 0 ? void 0 : statefulButtCons.map(function (statefulButtCon, index) {
        return jsx(SmartClone, {
          testId: statefulButtCon.props.testId || "".concat(testId, "__statefulButtCon"),
          children: statefulButtCon
        }, "statefulButtCon-".concat(
        // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
        index));
      }), drawerMenu ? jsx(SmartClone, {
        testId: drawerMenu.props.testId || "".concat(testId, "__drawerMenu"),
        children: drawerMenu
      }) : popoverMenu ? jsx(SmartClone, {
        testId: popoverMenu.props.testId || "".concat(testId, "__popoverMenu"),
        children: popoverMenu
      }) : null, (badge || intentIcon || otherChildren) && jsxs(Stack, {
        direction: "row",
        alignItems: "center",
        gap: "base.spacing.x1",
        rc: jsx("span", {}),
        children: [badge && jsx(SmartClone, {
          testId: badge.props.testId || "".concat(testId, "__badge"),
          children: badge
        }), otherChildren, intentIcon && jsx(SmartClone, {
          testId: intentIcon.props.testId || "".concat(testId, "__intentIcon"),
          children: intentIcon
        })]
      })]
    }) : null]
  });
  return jsx(ClassNames, {
    children: function children(_ref3) {
      var cx = _ref3.cx;
      return isBaseClickable(onClick, rc, containsClickables) ? jsx(BaseClickable, _objectSpread(_objectSpread(_objectSpread({}, !controlledHover ? {
        onMouseEnter: function onMouseEnter() {
          return setIsHovering(true);
        },
        onMouseLeave: function onMouseLeave() {
          return setIsHovering(false);
        }
      } : {}), baseClickableProps), {}, {
        testId: testId,
        className: cx({
          "MenuItem--selected": selected,
          "MenuItem--emphasized": emphasized,
          "MenuItem--hover": isHovering
        }, "".concat(className !== null && className !== void 0 ? className : "", " MenuItem")),
        sx: allStyles,
        children: innerContents
      })) : isBox(onClick, rc, containsClickables) ? jsx(Box, _objectSpread(_objectSpread(_objectSpread({}, !controlledHover ? {
        onMouseEnter: function onMouseEnter() {
          return setIsHovering(true);
        },
        onMouseLeave: function onMouseLeave() {
          return setIsHovering(false);
        }
      } : {}), boxProps), {}, {
        // @TODO: think we should use classes for this, rather than different
        // root dom elements - its more expressive and easier to follow
        // (eg copy the way that the Chip component works)
        rc: onClick ? jsx("div", {}) : jsx("article", {}),
        testId: testId,
        className: cx({
          "MenuItem--selected": selected,
          "MenuItem--emphasized": emphasized,
          "MenuItem--hover": isHovering
        }, "".concat(className !== null && className !== void 0 ? className : "", " MenuItem")),
        sx: allStyles,
        children: innerContents
      })) : null;
    }
  });
}
function MenuItem(_ref4) {
  var _ref4$shimmer = _ref4.shimmer,
    shimmer = _ref4$shimmer === void 0 ? false : _ref4$shimmer,
    _ref4$size = _ref4.size,
    size = _ref4$size === void 0 ? DEFAULT_MENU_ITEM_SIZE : _ref4$size,
    rc = _ref4.rc,
    testId = _ref4.testId,
    className = _ref4.className,
    children = _ref4.children,
    emphasized = _ref4.emphasized,
    selected = _ref4.selected,
    _ref4$sx = _ref4.sx,
    sx = _ref4$sx === void 0 ? {} : _ref4$sx,
    _ref4$shimmerSx = _ref4.shimmerSx,
    shimmerSx = _ref4$shimmerSx === void 0 ? {} : _ref4$shimmerSx,
    controlledHover = _ref4.controlledHover,
    textAlign = _ref4.textAlign,
    otherProps = _objectWithoutProperties(_ref4, _excluded2);
  return shimmer ? jsx(MenuItemShimmer, {
    size: size,
    rc: rc,
    testId: "".concat(testId, "__shimmer"),
    className: className,
    emphasized: emphasized,
    sx: shimmerSx
  }) : jsx(MenuItemContent, _objectSpread(_objectSpread({}, otherProps), {}, {
    controlledHover: controlledHover,
    rc: rc,
    size: size,
    testId: testId,
    className: className,
    emphasized: emphasized,
    selected: selected,
    sx: sx,
    textAlign: textAlign,
    children: children
  }));
}
MenuItem.displayName = "MenuItem";
MenuItem.Icon = MenuItemIcon;
MenuItem.FramedIcon = MenuItemFramedIcon;
MenuItem.FramedImage = MenuItemFramedImage;
MenuItem.FramedLogo = MenuItemFramedLogo;
MenuItem.Label = MenuItemLabel;
MenuItem.IntentIcon = MenuItemIntentIcon;
MenuItem.PriceDisplay = MenuItemPriceDisplay;
MenuItem.Caption = MenuItemCaption;
MenuItem.Badge = MenuItemBadge;
MenuItem.OverflowPopoverMenu = MenuItemOverflowPopoverMenu;
MenuItem.OverflowDrawerMenu = MenuItemOverflowDrawerMenu;
MenuItem.StatefulButtCon = MenuItemStatefulButtCon;
MenuItem.SwapStack = MenuItemSwapStack;
MenuItem.FramedStack = MenuItemFramedStack;
MenuItem.Avatar = MenuItemAvatar;
function MenuItemOverflowPopoverMenu(_ref5) {
  var children = _ref5.children,
    _ref5$sx = _ref5.sx,
    sx = _ref5$sx === void 0 ? {} : _ref5$sx,
    props = _objectWithoutProperties(_ref5, _excluded3);
  return jsx(OverflowPopoverMenu, _objectSpread(_objectSpread({}, props), {}, {
    size: "small",
    sx: merge({
      flexShrink: 0
    }, sx),
    children: children
  }));
}
MenuItemOverflowPopoverMenu.displayName = "MenuItem.OverflowPopoverMenu";
function MenuItemOverflowDrawerMenu(_ref6) {
  var children = _ref6.children,
    _ref6$sx = _ref6.sx,
    sx = _ref6$sx === void 0 ? {} : _ref6$sx,
    props = _objectWithoutProperties(_ref6, _excluded4);
  return jsx(OverflowDrawerMenu, _objectSpread(_objectSpread({}, props), {}, {
    size: "small",
    sx: merge({
      flexShrink: 0
    }, sx),
    children: children
  }));
}
MenuItemOverflowDrawerMenu.displayName = "MenuItem.OverflowDrawerMenu";

/*









      EXPANDABLE MENU ITEM








      
*/

function ExpandableMenuItem(props) {
  var _nestedVerticalMenu$p;
  var _ref7 = "expanded" in props ? _objectSpread(_objectSpread({}, props), {}, {
      defaultExpanded: undefined
    }) : _objectSpread(_objectSpread({}, props), {}, {
      expanded: undefined
    }),
    children = _ref7.children,
    _ref7$size = _ref7.size,
    size = _ref7$size === void 0 ? DEFAULT_MENU_ITEM_SIZE : _ref7$size,
    _ref7$emphasized = _ref7.emphasized,
    emphasized = _ref7$emphasized === void 0 ? false : _ref7$emphasized,
    textAlign = _ref7.textAlign,
    expanded = _ref7.expanded,
    defaultExpanded = _ref7.defaultExpanded,
    testId = _ref7.testId,
    onExpandChange = _ref7.onExpandChange,
    className = _ref7.className,
    shimmer = _ref7.shimmer,
    shimmerSx = _ref7.shimmerSx,
    _ref7$sx = _ref7.sx,
    sx = _ref7$sx === void 0 ? {} : _ref7$sx,
    otherProps = _objectWithoutProperties(_ref7, _excluded5);
  var theme = useTheme();
  var base = theme.base;
  var _useState3 = useState(defaultExpanded),
    _useState4 = _slicedToArray(_useState3, 2),
    uncontrolledExpanded = _useState4[0],
    setUncontrolledExpanded = _useState4[1];
  var expandedValueToUse = expanded !== null && expanded !== void 0 ? expanded : uncontrolledExpanded;
  var nestedVerticalMenu = useGetSubcomponentChild(children, VerticalMenu);
  var _useSplitApartChildre2 = useSplitApartChildrenAndSubComponents(children, [
    // @NOTE: its not possible to put these components into a MenuItem, so make sure they are
    // picked out of the children, before passing them on to MenuItem
    VerticalMenu, MenuItemIntentIcon, MenuItemStatefulButtCon]),
    otherChildren = _useSplitApartChildre2.otherChildren;
  var startingSize = getStartingSize(size, DEFAULT_MENU_ITEM_SIZE, MENU_ITEM_SIZES);
  var allStyles = merge({}, containerSx, renderSizeBasedContainerStyles({
    size: startingSize,
    theme: theme
  }), renderContainerResponsiveStyles({
    size: size,
    theme: theme
  }), sx);
  var handleToggle = useCallback(function () {
    if (shimmer) return;
    if (typeof expanded === "undefined") {
      setUncontrolledExpanded(function (old) {
        onExpandChange === null || onExpandChange === void 0 ? void 0 : onExpandChange(!old);
        return !old;
      });
    } else {
      onExpandChange === null || onExpandChange === void 0 ? void 0 : onExpandChange(!expanded);
    }
  }, [expanded, onExpandChange, shimmer]);
  return jsxs(Stack, _objectSpread(_objectSpread({}, otherProps), {}, {
    onClick: handleToggle,
    direction: "column",
    gap: "base.spacing.x1",
    sx: allStyles,
    className: "".concat(className !== null && className !== void 0 ? className : "", " ExpandableMenuItem"),
    testId: testId,
    children: [jsxs(MenuItem, {
      size: size,
      emphasized: emphasized,
      textAlign: textAlign,
      shimmer: shimmer,
      shimmerSx: shimmerSx
      // @NOTE: ensure that hoverFX are automatically given to any
      // ExpandableMenuItem component (to indicate interactivity)
      ,
      onClick: noop,
      sx: {
        position: "relative"
      },
      selected: expandedValueToUse,
      testId: "".concat(testId, "__menuItem"),
      children: [otherChildren, jsx(MenuItem.IntentIcon, {
        sx: {
          opacity: 0
        }
      }), jsx(SvgIcon, {
        className: "chevron",
        sx: rightChevronSx,
        rc: jsx(motion.svg, {}),
        children: jsx(motion.path, {
          initial: "down",
          animate: expandedValueToUse ? "up" : "down",
          variants: {
            down: {
              d: "M5 9L12 16L19 9"
            },
            up: {
              d: "M5 16L12 9L19 16"
            }
          },
          transition: {
            ease: base.motion.normal.fast.jsEase,
            duration: base.motion.normal.fast.jsDuration
          }
        })
      })]
    }), jsx(AnimatePresence, {
      children: Boolean(nestedVerticalMenu) && expandedValueToUse ? jsx(SmartClone, {
        rc: jsx(motion.div, {
          initial: {
            opacity: 0,
            height: 0,
            overflow: "hidden"
          },
          animate: {
            height: "auto",
            opacity: 1,
            transitionEnd: {
              overflow: "visible"
            }
          },
          exit: {
            height: 0,
            opacity: 0,
            overflow: "hidden"
          },
          transition: {
            duration: base.motion.normal.fast.jsDuration,
            ease: base.motion.normal.fast.jsEase
          }
        }),
        onClick: function onClick(event) {
          return event.stopPropagation();
        },
        emphasized: emphasized,
        sx: merge(nestedVerticalMenuSx, (nestedVerticalMenu === null || nestedVerticalMenu === void 0 ? void 0 : nestedVerticalMenu.props.sx) || {}),
        testId: (_nestedVerticalMenu$p = nestedVerticalMenu === null || nestedVerticalMenu === void 0 ? void 0 : nestedVerticalMenu.props.testId) !== null && _nestedVerticalMenu$p !== void 0 ? _nestedVerticalMenu$p : "".concat(testId, "__nestedVerticalMenu"),
        children: nestedVerticalMenu
      }) : null
    })]
  }));
}
ExpandableMenuItem.displayName = "ExpandableMenuItem";
ExpandableMenuItem.Icon = MenuItemIcon;
ExpandableMenuItem.FramedIcon = MenuItemFramedIcon;
ExpandableMenuItem.FramedImage = MenuItemFramedImage;
ExpandableMenuItem.FramedLogo = MenuItemFramedLogo;
ExpandableMenuItem.FramedStack = MenuItemFramedStack;
ExpandableMenuItem.Avatar = MenuItemAvatar;
ExpandableMenuItem.SwapStack = MenuItemSwapStack;
ExpandableMenuItem.Label = MenuItemLabel;
ExpandableMenuItem.Caption = MenuItemCaption;
ExpandableMenuItem.Badge = MenuItemBadge;

/*









      VERTICAL MENU








      
*/

var DEFAULT_VERTICAL_MENU_SIZE = "xSmall";
function VerticalMenu(_ref8) {
  var children = _ref8.children,
    _ref8$sx = _ref8.sx,
    sx = _ref8$sx === void 0 ? {} : _ref8$sx,
    _ref8$size = _ref8.size,
    size = _ref8$size === void 0 ? DEFAULT_VERTICAL_MENU_SIZE : _ref8$size,
    emphasized = _ref8.emphasized,
    _ref8$textAlign = _ref8.textAlign,
    textAlign = _ref8$textAlign === void 0 ? DEFAULT_TEXT_INPUT_ALIGN : _ref8$textAlign,
    className = _ref8.className,
    props = _objectWithoutProperties(_ref8, _excluded6);
  var flattenedChildren = useMemo(function () {
    return flattenChildren(children);
  }, [children]);
  var currentSizeClass = useGetCurrentSizeClass(size, DEFAULT_VERTICAL_MENU_SIZE, MENU_ITEM_SIZES);
  return jsx(Box, _objectSpread(_objectSpread({}, props), {}, {
    sx: merge(vFlex, verticalMenuSxProps, _objectSpread({}, emphasized ? {
      boxShadow: "base.shadow.500"
    } : {}), sx),
    className: "".concat(className !== null && className !== void 0 ? className : "", " VerticalMenu VerticalMenu--").concat(currentSizeClass, " ").concat(emphasized ? "VerticalMenu--emphasized" : ""),
    children: Children.map(flattenedChildren, function (child) {
      if (isChildSubcomponent(child, MultiSelectOption) || isChildSubcomponent(child, SelectOption) || isChildSubcomponent(child, MenuItem) || isChildSubcomponent(child, InputBar) || isChildSubcomponent(child, ExpandableMenuItem)) {
        return jsx(SmartClone, {
          size: child.props.size ? child.props.size : size,
          textAlign: textAlign,
          sx: merge({
            "& .innerTextContainer": {
              alignItems: textAlign === "left" ? "flex-start" : "flex-end"
            }
          }, child.props.sx || {}),
          children: child
        });
      }
      if (isChildSubcomponent(child, Divider)) {
        var _child$props$sx;
        return jsx(SmartClone, {
          size: child.props.size ? child.props.size : "xSmall",
          sx: merge({
            my: "base.spacing.x1"
          }, (_child$props$sx = child.props.sx) !== null && _child$props$sx !== void 0 ? _child$props$sx : {}),
          children: child
        });
      }
      return null;
    })
  }));
}
VerticalMenu.displayName = "VerticalMenu";
VerticalMenu.Divider = Divider;
VerticalMenu.Item = MenuItem;

/*








    SELECT OPTION









*/

function SelectOption(_ref9) {
  var _ref9$size = _ref9.size,
    size = _ref9$size === void 0 ? "xSmall" : _ref9$size,
    onOptionClick = _ref9.onOptionClick,
    children = _ref9.children,
    optionKey = _ref9.optionKey,
    _ref9$sx = _ref9.sx,
    sx = _ref9$sx === void 0 ? {} : _ref9$sx,
    props = _objectWithoutProperties(_ref9, _excluded7);
  var handleOptionClick = useCallback(function (ev) {
    return onOptionClick === null || onOptionClick === void 0 ? void 0 : onOptionClick(ev, children, optionKey);
  }, [children, onOptionClick, optionKey]);
  return jsx(MenuItem, _objectSpread(_objectSpread({}, props), {}, {
    rc: jsx("button", {
      onClick: handleOptionClick,
      type: "button"
    }),
    size: size,
    sx: merge({
      "&:not(article).hover": {
        boxShadow: "none"
      }
    }, sx),
    children: children
  }));
}
SelectOption.displayName = "Select.Option";
SelectOption.Label = MenuItem.Label;
SelectOption.Caption = MenuItem.Caption;
SelectOption.Icon = MenuItem.Icon;
SelectOption.FramedImage = MenuItem.FramedImage;
SelectOption.FramedLogo = MenuItem.FramedLogo;
SelectOption.FramedIcon = MenuItem.FramedIcon;
SelectOption.PriceDisplay = MenuItem.PriceDisplay;

/*








    MULTI SELECT OPTION









*/

function MultiSelectOption(_ref10) {
  var _ref10$size = _ref10.size,
    size = _ref10$size === void 0 ? "xSmall" : _ref10$size,
    onOptionClick = _ref10.onOptionClick,
    children = _ref10.children,
    optionKey = _ref10.optionKey,
    _ref10$sx = _ref10.sx,
    sx = _ref10$sx === void 0 ? {} : _ref10$sx,
    className = _ref10.className,
    props = _objectWithoutProperties(_ref10, _excluded8);
  var handleOptionClick = useCallback(function (ev) {
    return onOptionClick === null || onOptionClick === void 0 ? void 0 : onOptionClick(ev.target.checked, optionKey);
  }, [onOptionClick, optionKey]);
  var mergedSx = useMemo(function () {
    return merge(multiSelectOptionSx, sx);
  }, [sx]);
  return jsx(InputBar, _objectSpread(_objectSpread({}, props), {}, {
    inputType: "checkbox",
    onChange: handleOptionClick,
    size: size,
    sx: mergedSx,
    className: "".concat(className !== null && className !== void 0 ? className : "", " MultiSelectOption"),
    children: children
  }));
}
MultiSelectOption.displayName = "MultiSelect.Option";
MultiSelectOption.Label = MenuItem.Label;
MultiSelectOption.Caption = MenuItem.Caption;
MultiSelectOption.Icon = MenuItem.Icon;
MultiSelectOption.FramedImage = MenuItem.FramedImage;
MultiSelectOption.FramedIcon = MenuItem.FramedIcon;
MultiSelectOption.Avatar = MenuItem.Avatar;

/* 










    INPUT BAR










*/

function InputBar(_ref11) {
  var children = _ref11.children,
    checked = _ref11.checked,
    size = _ref11.size,
    emphasized = _ref11.emphasized,
    disabled = _ref11.disabled,
    idProp = _ref11.id,
    _ref11$inputType = _ref11.inputType,
    inputType = _ref11$inputType === void 0 ? "toggle" : _ref11$inputType,
    _ref11$domRef = _ref11.domRef,
    domRef = _ref11$domRef === void 0 ? {
      current: null
    } : _ref11$domRef,
    _ref11$inputRef = _ref11.inputRef,
    inputRef = _ref11$inputRef === void 0 ? {
      current: null
    } : _ref11$inputRef,
    onChange = _ref11.onChange,
    testId = _ref11.testId,
    defaultChecked = _ref11.defaultChecked,
    _ref11$sx = _ref11.sx,
    sx = _ref11$sx === void 0 ? {} : _ref11$sx,
    textAlign = _ref11.textAlign,
    className = _ref11.className,
    rc = _ref11.rc,
    inputProps = _objectWithoutProperties(_ref11, _excluded9);
  var fallbackId = useId();
  var id = idProp !== null && idProp !== void 0 ? idProp : fallbackId;
  var localRef = useForwardLocalDomRef(domRef);
  var localInputRef = useForwardLocalDomRef(inputRef);
  var _useResizeObserver = useResizeObserver(localRef),
    width = _useResizeObserver.width,
    height = _useResizeObserver.height;
  var inputStyles = useMemo(function () {
    return {
      position: "absolute",
      width: "100%",
      height: "100%",
      "& .actualInput": {
        width: "".concat(width, "px"),
        height: "".concat(height, "px"),
        zIndex: 1,
        "&:hover:not(:disabled)": {
          "& + .toggleContainer, & + .checkContainer, & + .radioContainer": {
            boxShadow: "none"
          }
        }
      },
      "& .toggleContainer, & .checkContainer, & .radioContainer": {
        position: "absolute",
        right: "base.spacing.x4",
        top: "50%",
        translate: "0 -50%"
      }
    };
  }, [width, height]);
  var allInputProps = _objectSpread(_objectSpread({}, inputProps), {}, {
    id: id,
    testId: "".concat(testId, "__biomeInput"),
    onChange: onChange,
    defaultChecked: defaultChecked,
    checked: checked,
    disabled: disabled,
    sx: inputStyles,
    inputRef: localInputRef
  });
  var flattenedChildren = useMemo(function () {
    return flattenChildren(children);
  }, [children]);
  var mergedSx = useMemo(function () {
    return merge(inputBarBaseSx, sx);
  }, [sx]);
  return jsx(ClassNames, {
    children: function children(_ref12) {
      var cx = _ref12.cx;
      return jsxs(Box, {
        rc: rc,
        sx: mergedSx,
        testId: testId,
        className: cx(className, "InputBar", {
          "InputBar--disabled": disabled
        }),
        domRef: localRef,
        children: [inputType === "toggle" && jsx(Toggle, _objectSpread({}, allInputProps)), inputType === "checkbox" && jsx(Checkbox, _objectSpread({}, allInputProps)), inputType === "radio" && jsx(Radio, _objectSpread({}, allInputProps)), jsx(MenuItem, {
          size: size,
          emphasized: emphasized,
          textAlign: textAlign,
          testId: "".concat(testId, "__menuItem"),
          sx: {
            pr: inputType === "toggle" ? "base.spacing.x15" : inputType === "checkbox" ? "base.spacing.x9" : "base.spacing.x9"
          },
          children: Children.map(flattenedChildren, function (child) {
            if (isChildSubcomponent(child, MenuItem.Label)) {
              return /*#__PURE__*/cloneElement(child, {
                htmlFor: id,
                rc: jsx("label", {})
              });
            }
            return child;
          })
        })]
      });
    }
  });
}
InputBar.displayName = "InputBar";
InputBar.Icon = MenuItem.Icon;
InputBar.FramedIcon = MenuItem.FramedIcon;
InputBar.FramedImage = MenuItem.FramedImage;
InputBar.FramedLogo = MenuItem.FramedLogo;
InputBar.FramedStack = MenuItem.FramedStack;
InputBar.Avatar = MenuItem.Avatar;
InputBar.SwapStack = MenuItem.SwapStack;
InputBar.Label = MenuItem.Label;
InputBar.Caption = MenuItem.Caption;

/*









    OVEFLOW DRAWER MENU









*/

function OverflowDrawerMenu(_ref13) {
  var children = _ref13.children,
    domRef = _ref13.domRef,
    testId = _ref13.testId,
    _ref13$icon = _ref13.icon,
    icon = _ref13$icon === void 0 ? "MoreVertical" : _ref13$icon,
    _ref13$size = _ref13.size,
    size = _ref13$size === void 0 ? DEFAULT_BUTTON_SIZE : _ref13$size,
    _ref13$variant = _ref13.variant,
    variant = _ref13$variant === void 0 ? "tertiary" : _ref13$variant,
    _onClick = _ref13.onClick,
    _ref13$drawerSize = _ref13.drawerSize,
    drawerSize = _ref13$drawerSize === void 0 ? "half" : _ref13$drawerSize,
    _ref13$showHeaderBar = _ref13.showHeaderBar,
    showHeaderBar = _ref13$showHeaderBar === void 0 ? true : _ref13$showHeaderBar,
    headerBarTitle = _ref13.headerBarTitle,
    showBgOverlay = _ref13.showBgOverlay,
    _ref13$outsideClicksC = _ref13.outsideClicksClose,
    outsideClicksClose = _ref13$outsideClicksC === void 0 ? true : _ref13$outsideClicksC,
    _ref13$escapeKeyClose = _ref13.escapeKeyClose,
    escapeKeyClose = _ref13$escapeKeyClose === void 0 ? true : _ref13$escapeKeyClose,
    _ref13$drawerPosition = _ref13.drawerPosition,
    drawerPosition = _ref13$drawerPosition === void 0 ? DEFAULT_DRAWER_POSITION : _ref13$drawerPosition,
    buttConProps = _objectWithoutProperties(_ref13, _excluded10);
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    visible = _useState6[0],
    setVisible = _useState6[1];
  var typedProps = {
    headerBarTitle: headerBarTitle,
    showHeaderBar: showHeaderBar
  };
  var flattenedChildren = useMemo(function () {
    return flattenChildren(children);
  }, [children]);
  return jsxs(Drawer, _objectSpread(_objectSpread({}, typedProps), {}, {
    visible: visible,
    onCloseDrawer: function onCloseDrawer() {
      return setVisible(false);
    },
    outsideClicksClose: outsideClicksClose,
    escapeKeyClose: escapeKeyClose,
    size: drawerSize,
    showBgOverlay: showBgOverlay,
    domRef: domRef,
    drawerPosition: drawerPosition,
    testId: testId,
    children: [jsx(Drawer.Target, {
      children: jsx(ButtCon, _objectSpread(_objectSpread({}, buttConProps), {}, {
        testId: testId,
        icon: icon,
        size: size,
        variant: variant,
        onClick: function onClick(event) {
          event.preventDefault();
          event.stopPropagation();
          setVisible(!visible);
          _onClick === null || _onClick === void 0 ? void 0 : _onClick(event);
        }
      }))
    }), jsx(Drawer.Content, {
      // @NOTE: weird emotion/react css issue here, padding-bottom is
      // over-written by a `padding` definition inside of the
      // DrawerOverlay component, despite the object order being correct
      sx: {
        paddingX: "base.spacing.x2"
      },
      children: jsx(VerticalMenu, {
        testId: "".concat(testId, "__overflowDrawerMenu"),
        sx: {
          maxHeight: "100%"
        },
        children: Children.map(flattenedChildren, function (child) {
          var _typedChild$props;
          var typedChild = child;
          var shouldCloneWithOnClick = /*#__PURE__*/isValidElement(typedChild) && Boolean((_typedChild$props = typedChild.props) === null || _typedChild$props === void 0 ? void 0 : _typedChild$props.onClick);
          return shouldCloneWithOnClick ? jsx(SmartClone, {
            onClick: function onClick(event) {
              event.preventDefault();
              event.stopPropagation();
              setVisible(false);
            },
            children: child
          }) : child;
        })
      })
    })]
  }));
}
OverflowDrawerMenu.displayName = "OverflowDrawerMenu";

/*












      OVERFLOW POPOVER MENU












*/

function OverflowPopoverMenu(_ref14) {
  var children = _ref14.children,
    domRef = _ref14.domRef,
    testId = _ref14.testId,
    _ref14$icon = _ref14.icon,
    icon = _ref14$icon === void 0 ? "MoreVertical" : _ref14$icon,
    _ref14$size = _ref14.size,
    size = _ref14$size === void 0 ? "medium" : _ref14$size,
    _ref14$variant = _ref14.variant,
    variant = _ref14$variant === void 0 ? "tertiary" : _ref14$variant,
    onClick = _ref14.onClick,
    buttConProps = _objectWithoutProperties(_ref14, _excluded11);
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    popoverVisible = _useState8[0],
    setPopoverVisible = _useState8[1];
  var _useWindowSizeStore = useWindowSizeStore(function (store) {
      return store;
    }),
    windowSize = _useWindowSizeStore.state;
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(function () {
    return setPopoverVisible(false);
  }, [windowSize]);
  var handleTargetClick = useCallback(function (event) {
    event.preventDefault();
    event.stopPropagation();
    setPopoverVisible(!popoverVisible);
    onClick === null || onClick === void 0 ? void 0 : onClick(event);
  }, [onClick, popoverVisible]);
  var flattenedChildren = useMemo(function () {
    return flattenChildren(children);
  }, [children]);
  return jsxs(Popover, {
    position: {
      x: "right",
      y: "below"
    },
    visible: popoverVisible,
    onOutsideClick: function onOutsideClick() {
      return setPopoverVisible(false);
    },
    domRef: domRef,
    verticalGap: 8,
    children: [jsx(Popover.Target, {
      children: jsx(ButtCon, _objectSpread(_objectSpread({}, buttConProps), {}, {
        testId: testId,
        icon: icon,
        size: size,
        variant: variant,
        onClick: handleTargetClick
      }))
    }), jsx(Popover.Content, {
      children: jsx(VerticalMenu, {
        testId: "".concat(testId, "__overflowPopoverMenu"),
        emphasized: true,
        sx: {
          // @NOTE: if VerticalMenu is nested inside another
          // VerticalMenu, we want to not keep making the parent
          // VerticalMenu's larger as each child opens. :P
          "& .VerticalMenu": {
            minw: "unset"
          }
        },
        children: Children.map(flattenedChildren, function (child) {
          var typedChild = child;
          var shouldCloneWithOnClick = /*#__PURE__*/isValidElement(typedChild) && (Boolean(typedChild.props.onClick) || Boolean(typedChild.props.rc));
          return shouldCloneWithOnClick ? jsx(SmartClone, {
            onClick: function onClick(event) {
              event.stopPropagation();
              setPopoverVisible(false);
            },
            children: child
          }) : child;
        })
      })
    })]
  });
}
OverflowPopoverMenu.displayName = "OverflowPopoverMenu";

/*












      MULTI SELECT











*/

function MultiSelect(_ref15) {
  var _ref17;
  var children = _ref15.children,
    validationStatus = _ref15.validationStatus,
    _ref15$defaultLabel = _ref15.defaultLabel,
    defaultLabel = _ref15$defaultLabel === void 0 ? "Select options" : _ref15$defaultLabel,
    _ref15$sx = _ref15.sx,
    sx = _ref15$sx === void 0 ? {} : _ref15$sx,
    _ref15$popoverContain = _ref15.popoverContainerSx,
    popoverContainerSx = _ref15$popoverContain === void 0 ? {} : _ref15$popoverContain,
    _ref15$domRef = _ref15.domRef,
    domRef = _ref15$domRef === void 0 ? {
      current: null
    } : _ref15$domRef,
    onSelectChange = _ref15.onSelectChange,
    _ref15$testId = _ref15.testId,
    testId = _ref15$testId === void 0 ? "MultiSelect" : _ref15$testId,
    idProp = _ref15.id,
    _ref15$size = _ref15.size,
    size = _ref15$size === void 0 ? DEFAULT_SELECT_SIZE : _ref15$size,
    _ref15$textAlign = _ref15.textAlign,
    textAlign = _ref15$textAlign === void 0 ? DEFAULT_TEXT_INPUT_ALIGN : _ref15$textAlign,
    targetClickOveride = _ref15.targetClickOveride,
    showSelectAll = _ref15.showSelectAll,
    _ref15$selectedChipsW = _ref15.selectedChipsWrap,
    selectedChipsWrap = _ref15$selectedChipsW === void 0 ? false : _ref15$selectedChipsW,
    disabled = _ref15.disabled,
    className = _ref15.className,
    props = _objectWithoutProperties(_ref15, _excluded12);
  var _ref16 = "selectedOptions" in props ? _objectSpread(_objectSpread({}, props), {}, {
      defaultOptions: undefined
    }) : _objectSpread(_objectSpread({}, props), {}, {
      selectedOptions: undefined
    }),
    selectedOptions = _ref16.selectedOptions,
    defaultOptions = _ref16.defaultOptions,
    otherProps = _objectWithoutProperties(_ref16, _excluded13);
  var theme = useTheme();
  var _useState9 = useState(false),
    _useState10 = _slicedToArray(_useState9, 2),
    popoverVisible = _useState10[0],
    setPopoverVisible = _useState10[1];
  var options = useGetSubcomponentChildren(children, MultiSelectOption);
  var dedupedDefaultOptions = useMemo(function () {
    return defaultOptions !== undefined ? _toConsumableArray(new Set(defaultOptions)) : undefined;
  }, [defaultOptions]);
  var dedupedSelectedOptions = useMemo(function () {
    return selectedOptions !== undefined ? _toConsumableArray(new Set(selectedOptions)) : undefined;
  }, [selectedOptions]);
  var _useState11 = useState((_ref17 = dedupedSelectedOptions !== null && dedupedSelectedOptions !== void 0 ? dedupedSelectedOptions : dedupedDefaultOptions) !== null && _ref17 !== void 0 ? _ref17 : []),
    _useState12 = _slicedToArray(_useState11, 2),
    uncontrolledSelectedOptions = _useState12[0],
    setUncontrolledSelectedOptions = _useState12[1];
  var selectedOptionsToUse = useMemo(function () {
    return dedupedSelectedOptions !== null && dedupedSelectedOptions !== void 0 ? dedupedSelectedOptions : uncontrolledSelectedOptions;
  }, [dedupedSelectedOptions, uncontrolledSelectedOptions]);
  var fallbackId = useId();
  var id = idProp !== null && idProp !== void 0 ? idProp : fallbackId;
  var startingSize = useMemo(function () {
    return getStartingSize(size, DEFAULT_TEXT_INPUT_SIZE, TEXT_INPUT_SIZES);
  }, [size]);
  var leftIcon = useGetSubcomponentChild(children, Icon);
  var hasValue = selectedOptionsToUse.length > 0;
  var allButtonSx = useMemo(function () {
    return merge(inputContainerBaseSx, baseInputSx, renderContainerStyles({
      size: startingSize,
      theme: theme
    }), renderResponsiveContainerStyles({
      size: size,
      theme: theme
    }), renderInputSx({
      validationStatus: validationStatus,
      leftHandButtonsWidth: leftIcon ? 24 : 0,
      rightHandButtonsWidth: 0,
      textAlign: textAlign,
      theme: theme,
      hasValue: hasValue
    }), renderResponsiveInputSx({
      theme: theme,
      size: size,
      validationStatus: validationStatus,
      leftHandButtonsWidth: leftIcon ? 24 : 0,
      rightHandButtonsWidth: 0,
      textAlign: textAlign,
      hasValue: hasValue
    }), baseSelectSx, getSelectCustomSx({
      textAlign: textAlign,
      disabled: disabled,
      size: startingSize
    }), getResponsiveSelectCustomSx({
      theme: theme,
      size: size,
      textAlign: textAlign,
      disabled: disabled
    }), baseMultiSelectSx, sx);
  }, [leftIcon, size, startingSize, sx, textAlign, theme, validationStatus, disabled, hasValue]);
  var leftIconStyles = useMemo(function () {
    return merge(inputIconBaseSx, {
      mr: "0",
      svg: {
        width: "100%"
      }
    });
  }, []);
  var localTargetRef = useForwardLocalDomRef(domRef);
  var _useResizeObserver2 = useResizeObserver(localTargetRef, 30),
    width = _useResizeObserver2.width;
  var sizeAsClass = useGetCurrentSizeClass(size, DEFAULT_SELECT_SIZE, SELECT_SIZES);
  var handleOptionClick = useCallback(function (selected, optionKey) {
    if (selected) {
      var modifySelectedOptions = [].concat(_toConsumableArray(selectedOptionsToUse), [optionKey]);
      onSelectChange === null || onSelectChange === void 0 ? void 0 : onSelectChange(modifySelectedOptions);
      setUncontrolledSelectedOptions(modifySelectedOptions);
    } else {
      var _modifySelectedOptions = selectedOptionsToUse.filter(function (key) {
        return key !== optionKey;
      });
      onSelectChange === null || onSelectChange === void 0 ? void 0 : onSelectChange(_modifySelectedOptions);
      setUncontrolledSelectedOptions(_modifySelectedOptions);
    }
  }, [selectedOptionsToUse, onSelectChange]);
  var handleSelectAllToggle = useCallback(function (ev) {
    if (ev.target.checked) {
      var _options$map;
      var modifySelectedOptions = (_options$map = options === null || options === void 0 ? void 0 : options.map(function (option) {
        return option.props.optionKey;
      })) !== null && _options$map !== void 0 ? _options$map : [];
      onSelectChange === null || onSelectChange === void 0 ? void 0 : onSelectChange(modifySelectedOptions);
      return setUncontrolledSelectedOptions(modifySelectedOptions);
    }
    onSelectChange === null || onSelectChange === void 0 ? void 0 : onSelectChange([]);
    return setUncontrolledSelectedOptions([]);
  }, [options, onSelectChange]);
  var handleRemoveChip = useCallback(function (optionKey) {
    return function () {
      var modifySelectedOptions = selectedOptionsToUse.filter(function (key) {
        return key !== optionKey;
      });
      onSelectChange === null || onSelectChange === void 0 ? void 0 : onSelectChange(modifySelectedOptions);
      setUncontrolledSelectedOptions(modifySelectedOptions);
    };
  }, [onSelectChange, selectedOptionsToUse]);
  var selectAllIsChecked = useMemo(function () {
    return selectedOptionsToUse.length === (options === null || options === void 0 ? void 0 : options.length) && selectedOptionsToUse.length > 0;
  }, [selectedOptionsToUse, options]);

  // @NOTE: runtime check to ensure that the selectedOptions
  // prop is only ever set to optionKeys that are valid
  useEffect(function () {
    if (selectedOptions === undefined) {
      return;
    }
    var hasBadOptionKeys = selectedOptions.some(function (key) {
      return !(options !== null && options !== void 0 && options.some(function (option) {
        return option.props.optionKey === key;
      }));
    });
    if (hasBadOptionKeys) {
      warnUser(createMultiSelectErrorMessage(selectedOptions));
    }
  }, [selectedOptions, options]);
  return jsxs(Popover, {
    id: id,
    domRef: localTargetRef,
    position: {
      x: textAlign,
      y: "below"
    },
    visible: popoverVisible,
    onOutsideClick: function onOutsideClick() {
      return setPopoverVisible(false);
    },
    verticalGap: 0,
    children: [jsx(Popover.Target, {
      children: jsxs(Box, _objectSpread(_objectSpread({}, otherProps), {}, {
        testId: "".concat(testId, "__target"),
        sx: allButtonSx,
        onClick: function onClick(event) {
          if (targetClickOveride) {
            targetClickOveride === null || targetClickOveride === void 0 ? void 0 : targetClickOveride(event);
          } else {
            setPopoverVisible(!popoverVisible);
          }
        },
        className: "".concat(className !== null && className !== void 0 ? className : "", " MultiSelect MultiSelect--").concat(sizeAsClass, " MultiSelect--textAlign_").concat(textAlign, " ").concat(disabled ? "MultiSelect--disabled" : "", " ").concat(selectedChipsWrap ? "MultiSelect--selectedChipsWrap" : ""),
        children: [leftIcon && jsx(SmartClone, {
          testId: "".concat(testId, "__target__left"),
          sx: leftIconStyles,
          fallBackDomNode: "div",
          className: "MultiSelect__leftIcon",
          children: leftIcon
        }), selectedOptionsToUse.length === 0 ? jsx(Body, {
          size: "medium",
          testId: "".concat(testId, "__target__defaultLabel"),
          sx: defaultLabelSx,
          className: "MultiSelect__defaultLabel",
          children: defaultLabel
        }) : null, jsx(Stack, {
          rc: jsx("span", {}),
          sx: baseChipsContainerSx,
          gap: "base.spacing.x1",
          direction: "row",
          className: "MultiSelect__chipsContainer",
          testId: "".concat(testId, "__target__chips"),
          children: selectedOptionsToUse.map(function (optionKey) {
            var _getSubcomponentChild;
            var pickedOption = options === null || options === void 0 ? void 0 : options.find(function (opt) {
              return opt.props.optionKey === optionKey;
            });
            if (!pickedOption) return null;
            var label = getSubcomponentChild(pickedOption.props.children, MenuItem.Label);
            var icon = (_getSubcomponentChild = getSubcomponentChild(pickedOption.props.children, MenuItem.Icon)) !== null && _getSubcomponentChild !== void 0 ? _getSubcomponentChild : getSubcomponentChild(pickedOption.props.children, MenuItem.FramedIcon);
            var framedImage = getSubcomponentChild(pickedOption.props.children, MenuItem.FramedImage);
            var avatar = getSubcomponentChild(pickedOption.props.children, MenuItem.Avatar);
            var iconKey = /*#__PURE__*/isValidElement(icon) ? icon.props.icon : undefined;
            var labelText = /*#__PURE__*/isValidElement(label) ? label === null || label === void 0 ? void 0 : label.props.children : "";
            var framedImageProps = /*#__PURE__*/isValidElement(framedImage) ? framedImage.props : undefined;
            var _ref18 = framedImageProps !== null && framedImageProps !== void 0 ? framedImageProps : {
                sx: undefined
              };
              _ref18.sx;
              var otherFramedImageProps = _objectWithoutProperties(_ref18, _excluded14);
            var avatarProps = /*#__PURE__*/isValidElement(avatar) ? avatar.props : undefined;
            // @NOTE: we strip away size and sx props here, as that is set
            // automatically via the Chip component
            var _ref19 = avatarProps !== null && avatarProps !== void 0 ? avatarProps : {};
              _ref19.size;
              _ref19.sx;
              var otherAvatarProps = _objectWithoutProperties(_ref19, _excluded15);
            return jsxs(Chip, {
              label: labelText,
              sx: chipBaseSx,
              size: size,
              onRemove: handleRemoveChip(optionKey),
              rc: jsx("span", {}),
              testId: "".concat(testId, "__target__chips__chip--").concat(optionKey),
              children: [iconKey && jsx(Chip.Icon, {
                icon: iconKey
              }), framedImageProps && jsx(Chip.FramedImage, _objectSpread({}, otherFramedImageProps)), avatarProps && jsx(Chip.Avatar, _objectSpread({}, otherAvatarProps))]
            }, optionKey);
          })
        }), jsx(SvgIcon, {
          className: "MultiSelect__chevron",
          sx: selectChevronSx,
          rc: jsx(motion.svg, {}),
          children: jsx(motion.path, {
            initial: "down",
            variants: {
              down: {
                d: "M5 9L12 16L19 9"
              },
              up: {
                d: "M5 16L12 9L19 16"
              }
            },
            animate: popoverVisible ? "up" : "down",
            transition: {
              ease: theme.base.motion.normal.fast.jsEase,
              duration: theme.base.motion.normal.fast.jsDuration
            }
          })
        })]
      }))
    }), jsx(Popover.Content, {
      testId: "".concat(testId, "__content"),
      sx: merge({
        w: "".concat(width, "px"),
        bradbl: "base.borderRadius.x6",
        bradbr: "base.borderRadius.x6"
      }, popoverContainerSx),
      children: jsxs(VerticalMenu, {
        textAlign: textAlign,
        sx: {
          minw: "unset"
        },
        testId: "".concat(testId, "__content__verticalMenu"),
        emphasized: true,
        children: [showSelectAll && jsxs(Fragment, {
          children: [jsx(InputBar, {
            sx: multiSelectOptionSx,
            inputType: "checkbox",
            testId: "".concat(testId, "__content__verticalMenu__selectAll"),
            size: "xSmall",
            onChange: handleSelectAllToggle,
            checked: selectAllIsChecked,
            className: "MultiSelectOption",
            children: jsx(InputBar.Label, {
              children: "Select all"
            })
          }), jsx(VerticalMenu.Divider, {
            size: "xSmall",
            sx: {
              my: "unset"
            }
          })]
        }), Children.map(options, function (child) {
          var _child$props$optionKe, _child$props;
          var optionKey = (_child$props$optionKe = child === null || child === void 0 || (_child$props = child.props) === null || _child$props === void 0 ? void 0 : _child$props.optionKey) !== null && _child$props$optionKe !== void 0 ? _child$props$optionKe : "";
          return jsx(SmartClone, {
            onOptionClick: handleOptionClick,
            checked: selectedOptionsToUse.includes(optionKey),
            testId: "".concat(testId, "__content__verticalMenu__option--").concat(optionKey),
            children: child
          });
        })]
      })
    })]
  });
}
MultiSelect.displayName = "MultiSelect";
MultiSelect.Option = MultiSelectOption;
MultiSelect.Icon = Icon;

/*












      SELECT











*/

function useGetControlledLeftContent(_ref20) {
  var options = _ref20.options,
    selectedOption = _ref20.selectedOption;
  var controlledSelectionFramedImage = useGetControlledContent({
    options: options,
    selectedOption: selectedOption,
    subcomponent: SelectOption.FramedImage
  });
  var controlledSelectionIcon = useGetControlledContent({
    options: options,
    selectedOption: selectedOption,
    subcomponent: SelectOption.Icon
  });
  var controlledSelectionFramedIcon = useGetControlledContent({
    options: options,
    selectedOption: selectedOption,
    subcomponent: SelectOption.FramedIcon
  });
  var controlledSelectionFramedLogo = useGetControlledContent({
    options: options,
    selectedOption: selectedOption,
    subcomponent: SelectOption.FramedLogo
  });
  return controlledSelectionFramedIcon || controlledSelectionFramedImage || controlledSelectionFramedLogo || controlledSelectionIcon || null;
}
function Select(_ref21) {
  var children = _ref21.children,
    validationStatus = _ref21.validationStatus,
    _ref21$defaultLabel = _ref21.defaultLabel,
    defaultLabel = _ref21$defaultLabel === void 0 ? "Select an option" : _ref21$defaultLabel,
    _ref21$sx = _ref21.sx,
    sx = _ref21$sx === void 0 ? {} : _ref21$sx,
    _ref21$popoverContain = _ref21.popoverContainerSx,
    popoverContainerSx = _ref21$popoverContain === void 0 ? {} : _ref21$popoverContain,
    onSelectChange = _ref21.onSelectChange,
    _ref21$domRef = _ref21.domRef,
    domRef = _ref21$domRef === void 0 ? {
      current: null
    } : _ref21$domRef,
    _ref21$testId = _ref21.testId,
    testId = _ref21$testId === void 0 ? "Select" : _ref21$testId,
    idProp = _ref21.id,
    _ref21$size = _ref21.size,
    size = _ref21$size === void 0 ? DEFAULT_SELECT_SIZE : _ref21$size,
    _ref21$textAlign = _ref21.textAlign,
    textAlign = _ref21$textAlign === void 0 ? DEFAULT_TEXT_INPUT_ALIGN : _ref21$textAlign,
    targetClickOveride = _ref21.targetClickOveride,
    disabled = _ref21.disabled,
    className = _ref21.className,
    props = _objectWithoutProperties(_ref21, _excluded16);
  var _ref22 = "selectedOption" in props ? _objectSpread(_objectSpread({}, props), {}, {
      defaultOption: undefined
    }) : _objectSpread(_objectSpread({}, props), {}, {
      selectedOption: undefined
    }),
    selectedOption = _ref22.selectedOption,
    defaultOption = _ref22.defaultOption,
    otherProps = _objectWithoutProperties(_ref22, _excluded17);
  var theme = useTheme();
  var _useState13 = useState(null),
    _useState14 = _slicedToArray(_useState13, 2),
    selectedOptionChildren = _useState14[0],
    setSelectedOptionChildren = _useState14[1];
  var localRef = useForwardLocalDomRef(domRef);
  var _useState15 = useState(false),
    _useState16 = _slicedToArray(_useState15, 2),
    popoverVisible = _useState16[0],
    setPopoverVisible = _useState16[1];
  var options = useGetSubcomponentChildren(children, SelectOption);
  var defaultIcon = useGetSubcomponentChild(children, SelectDefaultIcon);
  var defaultFramedImage = useGetSubcomponentChild(children, SelectDefaultFramedImage);
  var defaultFramedLogo = useGetSubcomponentChild(children, SelectDefaultFramedLogo);
  var targetSize = useResizeObserver(localRef, 30);
  var fallbackId = useId();
  var id = idProp !== null && idProp !== void 0 ? idProp : fallbackId;
  var _useWindowSizeStore2 = useWindowSizeStore(function (store) {
      return store;
    }),
    windowWidth = _useWindowSizeStore2.state.width;

  // @NOTE: collect up specific children, depending on the current
  // state of the Select component
  var _useMemo = useMemo(function () {
      return {
        selectedOptionFramedImage: getSubcomponentChild(selectedOptionChildren, SelectOption.FramedImage),
        selectedOptionIcon: getSubcomponentChild(selectedOptionChildren, SelectOption.Icon),
        selectedOptionText: getSubcomponentChild(selectedOptionChildren, SelectOption.Label),
        selectedOptionFramedIcon: getSubcomponentChild(selectedOptionChildren, SelectOption.FramedIcon),
        selectedOptionFramedLogo: getSubcomponentChild(selectedOptionChildren, SelectOption.FramedLogo)
      };
    }, [selectedOptionChildren]),
    selectedOptionFramedImage = _useMemo.selectedOptionFramedImage,
    selectedOptionIcon = _useMemo.selectedOptionIcon,
    selectedOptionText = _useMemo.selectedOptionText,
    selectedOptionFramedIcon = _useMemo.selectedOptionFramedIcon,
    selectedOptionFramedLogo = _useMemo.selectedOptionFramedLogo;

  // @NOTE: setup controlled API selection children
  var controlledLeftSideContent = useGetControlledLeftContent({
    options: options,
    selectedOption: selectedOption
  });
  var controlledSelectionText = useMemo(function () {
    if (selectedOption === null) return null;
    var pickedOption = pickAnOption(selectedOption, options);
    if (pickedOption !== null && pickedOption !== void 0 && pickedOption.props.children) {
      return getSubcomponentChild(pickedOption.props.children, SelectOption.Label);
    }
    if (selectedOption !== undefined) {
      warnUser(noOptionFoundErrorText(selectedOption));
    }
    return false;
  }, [options, selectedOption]);
  var startingSize = useMemo(function () {
    return getStartingSize(size, DEFAULT_TEXT_INPUT_SIZE, TEXT_INPUT_SIZES);
  }, [size]);
  var leftSideContent = useMemo(function () {
    return controlledLeftSideContent || selectedOptionIcon || selectedOptionFramedImage || selectedOptionFramedIcon || selectedOptionFramedLogo || defaultIcon || defaultFramedImage || defaultFramedLogo || null;
  }, [controlledLeftSideContent, selectedOptionFramedImage, selectedOptionIcon, selectedOptionFramedIcon, selectedOptionFramedLogo, defaultFramedImage, defaultIcon, defaultFramedLogo]);
  var allButtonSx = useMemo(function () {
    return merge(inputContainerBaseSx, baseInputSx, renderContainerStyles({
      size: startingSize,
      theme: theme
    }), renderResponsiveContainerStyles({
      size: size,
      theme: theme
    }), renderInputSx({
      validationStatus: validationStatus,
      leftHandButtonsWidth: leftSideContent ? 24 : 0,
      rightHandButtonsWidth: 0,
      textAlign: textAlign,
      theme: theme,
      hasValue: Boolean(selectedOptionText || controlledSelectionText)
    }), renderResponsiveInputSx({
      theme: theme,
      size: size,
      validationStatus: validationStatus,
      leftHandButtonsWidth: leftSideContent ? 24 : 0,
      rightHandButtonsWidth: 0,
      textAlign: textAlign,
      hasValue: Boolean(selectedOptionText || controlledSelectionText)
    }), baseSelectSx, getSelectCustomSx({
      textAlign: textAlign,
      disabled: disabled,
      size: startingSize
    }), getResponsiveSelectCustomSx({
      theme: theme,
      size: size,
      textAlign: textAlign,
      disabled: disabled
    }), sx);
  }, [leftSideContent, size, startingSize, sx, textAlign, theme, validationStatus, disabled, selectedOptionText, controlledSelectionText]);
  var leftContentStyles = useMemo(function () {
    var leftSideSx = {};
    if ( /*#__PURE__*/isValidElement(leftSideContent)) {
      var _typedProps$sx;
      var typedProps = leftSideContent.props;
      leftSideSx = (_typedProps$sx = typedProps.sx) !== null && _typedProps$sx !== void 0 ? _typedProps$sx : {};
    }
    return merge(inputIconBaseSx, {
      mr: "0",
      svg: {
        width: "100%"
      }
    }, leftSideSx);
  }, [leftSideContent]);
  var sizeAsClass = useGetCurrentSizeClass(size, DEFAULT_SELECT_SIZE, SELECT_SIZES);

  // @NOTE: life cycle stuff below here:
  // ------------------------------------------------------------------

  useBrowserLayoutEffect(function () {
    // @NOTE: if the popover is visible, and the window width changes,
    // close it, so it doesnt look out of place ...
    setPopoverVisible(false);
  }, [windowWidth, setPopoverVisible]);

  // @NOTE: When selectedOption is null, we need to clear out any
  // selected option children, BUT ONLY when we're in controlled mode
  useBrowserEffect(function () {
    if (selectedOption === null && selectedOptionChildren) {
      setSelectedOptionChildren(null);
    }
  }, [selectedOption, selectedOptionChildren]);
  useBrowserEffect(function () {
    if (defaultOption !== undefined) {
      var pickedOption = pickAnOption(defaultOption, options);
      setSelectedOptionChildren(pickedOption === null || pickedOption === void 0 ? void 0 : pickedOption.props.children);
    }
  }, [defaultOption]);
  var labelContent = useMemo(function () {
    if (controlledSelectionText || selectedOptionText) {
      return jsx(SmartClone, {
        testId: "".concat(testId, "__target__label"),
        size: "small",
        children: controlledSelectionText || selectedOptionText
      });
    }
    return jsx(Body, {
      size: "medium",
      testId: "".concat(testId, "__target__label"),
      sx: {
        c: "base.color.text.body.secondary"
      },
      children: defaultLabel
    });
  }, [controlledSelectionText, defaultLabel, selectedOptionText, testId]);
  return jsxs(Popover, {
    id: id,
    domRef: localRef,
    position: {
      x: textAlign,
      y: "below"
    },
    visible: popoverVisible,
    onOutsideClick: function onOutsideClick() {
      return setPopoverVisible(false);
    },
    verticalGap: 0,
    children: [jsx(Popover.Target, {
      children: jsxs(BaseClickable, _objectSpread(_objectSpread({}, otherProps), {}, {
        disabled: disabled,
        testId: "".concat(testId, "__target"),
        sx: allButtonSx,
        onClick: function onClick(event) {
          if (targetClickOveride) {
            targetClickOveride === null || targetClickOveride === void 0 ? void 0 : targetClickOveride(event);
          } else {
            setPopoverVisible(!popoverVisible);
          }
        },
        className: "".concat(className !== null && className !== void 0 ? className : "", " Select Select--").concat(sizeAsClass, " Select--textAlign_").concat(textAlign, " ").concat(disabled ? "Select--disabled" : ""),
        children: [leftSideContent && jsx(SmartClone, {
          testId: "".concat(testId, "__target__left"),
          sx: leftContentStyles,
          children: leftSideContent
        }), labelContent, jsx(SvgIcon, {
          className: "chevron",
          sx: selectChevronSx,
          rc: jsx(motion.svg, {}),
          children: jsx(motion.path, {
            initial: "down",
            variants: {
              down: {
                d: "M5 9L12 16L19 9"
              },
              up: {
                d: "M5 16L12 9L19 16"
              }
            },
            animate: popoverVisible ? "up" : "down",
            transition: {
              ease: theme.base.motion.normal.fast.jsEase,
              duration: theme.base.motion.normal.fast.jsDuration
            }
          })
        })]
      }))
    }), jsx(Popover.Content, {
      testId: "".concat(testId, "__content"),
      sx: merge({
        w: "".concat(targetSize.width, "px"),
        bradbl: "base.borderRadius.x6",
        bradbr: "base.borderRadius.x6"
      }, popoverContainerSx),
      children: jsx(VerticalMenu, {
        textAlign: textAlign,
        sx: {
          minw: "unset"
        },
        testId: "".concat(testId, "__content__verticalMenu"),
        emphasized: true,
        children: Children.map(options, function (child) {
          return jsx(SmartClone, {
            onOptionClick: function onOptionClick(ev, childrenEls, optionKey) {
              var _child$props$onClick, _child$props2;
              child === null || child === void 0 || (_child$props$onClick = (_child$props2 = child.props).onClick) === null || _child$props$onClick === void 0 ? void 0 : _child$props$onClick.call(_child$props2, ev);
              setPopoverVisible(false);
              setSelectedOptionChildren(childrenEls);
              onSelectChange === null || onSelectChange === void 0 ? void 0 : onSelectChange(optionKey);
            },
            children: child
          });
        })
      })
    })]
  });
}
Select.displayName = "Select";
Select.Option = SelectOption;
Select.DefaultIcon = SelectDefaultIcon;
Select.DefaultFramedImage = SelectDefaultFramedImage;
Select.DefaultFramedLogo = SelectDefaultFramedLogo;

export { DEFAULT_VERTICAL_MENU_SIZE as D, ExpandableMenuItem as E, InputBar as I, MenuItem as M, OverflowDrawerMenu as O, SelectOption as S, VerticalMenu as V, MenuItemOverflowPopoverMenu as a, MenuItemOverflowDrawerMenu as b, MultiSelectOption as c, OverflowPopoverMenu as d, MultiSelect as e, Select as f, selectChevronSx as s };
//# sourceMappingURL=index.js.map
